/**
 * RewritePowerModal Component
 * Multi-step wizard for rewriting powers
 * Supports One-to-One, Many-to-One, and One-to-Many rewrites
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  ArrowsRightLeftIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
  PlusIcon,

  ChevronDownIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner, OathAlert, OathConfirmDialog } from '../../../shared/OathUI';
import { toast } from 'react-toastify';
import { useOathAuth } from '../../../../services/oathAuthContext';
import {
  getAvailablePowersForRewrite,
  lookupExecutedPowerForRewrite,
  validateRewrite,
  executeSubmittedRewrite
} from '../../../../services/oathPowerReportService';
import {
  formatSSN,
  formatCurrencyWithSymbol,
  formatCurrencyInput,
  parseCurrencyInput,
  formatDateForInput,
  formatDateForDisplay,
  formatDateInputText,
  parseDateInput,
  formatCurrency,
  toTitleCase
} from '../utils';
import {
  REWRITE_STEPS,
  REWRITE_TYPES,
  REWRITE_TYPE_CONFIG,
  VALIDATION_RULES,
  REPORT_STATUS,
  CASE_NUMBER_REQUIRED_STATUSES,
  SPECIAL_DISCHARGE_STATES,
  SEARCH_DEBOUNCE_MS,
  STATUS_LABELS
} from '../utils/constants';
import { useAgentsCitiesCache } from '../utils/hooks';

// Pure utility - no state dependencies, hoisted outside component
const lettersOnly = (str) => {
  if (!str) return str;
  return str.replace(/[^a-zA-Z0-9\s\-']/g, '');
};

// Replacement powers come back in any reportable status (U, UF, UJ, UD). Plain 'U' is the
// normal case and needs no callout; flag the others so the agent knows the power already
// carries a forfeiture / judgment / discharge that will follow it through the rewrite.
const ReplacementStatusBadge = ({ status, className = '' }) => {
  if (!status || status === 'U') return null;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 ${className}`}
      title={`Power status: ${status}`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
};

// Normalize city/name strings for fuzzy map lookups (strips spaces, hyphens, apostrophes)
const normalizeForLookup = (str) => (str || '').toLowerCase().replace(/[\s\-']/g, '');

// Static step configuration - hoisted to avoid re-creation every render (rendering-hoist-jsx)
const STEP_CONFIG = [
  { step: REWRITE_STEPS.SELECT_TYPE, label: 'Type' },
  { step: REWRITE_STEPS.SELECT_ORIGINALS, label: 'Original' },
  { step: REWRITE_STEPS.SELECT_REPLACEMENT, label: 'Replacement' },
  { step: REWRITE_STEPS.ENTER_DETAILS, label: 'Details' },
  { step: REWRITE_STEPS.REVIEW, label: 'Review' }
];

// Reusable keyboard navigation for searchable dropdowns (DRY extraction)
const useDropdownKeyboard = (isOpen, setOpen, items, highlighted, setHighlighted, onSelect) => {
  return useCallback((e) => {
    if (!isOpen || items.length === 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setOpen(true);
        setHighlighted(0);
        e.preventDefault();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlighted(prev => prev < items.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlighted(prev => prev > 0 ? prev - 1 : items.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlighted >= 0 && highlighted < items.length) onSelect(items[highlighted]);
        break;
      case 'Tab':
        if (highlighted >= 0 && highlighted < items.length) onSelect(items[highlighted]);
        setOpen(false);
        break;
      case 'Escape':
        setOpen(false);
        setHighlighted(-1);
        break;
      default:
        break;
    }
  }, [isOpen, items, highlighted, setOpen, setHighlighted, onSelect]);
};

const RewritePowerModal = ({
  isOpen,
  onClose,
  onSuccess,
  reportId,
  reportStatus = REPORT_STATUS.DRAFT,
  powers = [], // All powers in the report (replaces originalPower)
  agencyId: propAgencyId = null,
  preloadedAgents = [],
  preloadedCities = []
}) => {
  const { user } = useOathAuth();
  const { getCachedAgents, getCachedCities } = useAgentsCitiesCache();

  // Get agency ID from prop or user
  const agencyId = propAgencyId || user?.agencies?.[0]?.agencyId;

  // Wizard step state
  const [currentStep, setCurrentStep] = useState(REWRITE_STEPS.SELECT_TYPE);

  // Rewrite type selection
  const [rewriteType, setRewriteType] = useState(null);

  // Original power(s) - now manually entered and validated via API
  const [selectedOriginalPowers, setSelectedOriginalPowers] = useState([]);
  const [originalPowerInput, setOriginalPowerInput] = useState(''); // Manual input for power number
  const [lookingUpOriginal, setLookingUpOriginal] = useState(false); // Loading state for power lookup
  const [originalLookupError, setOriginalLookupError] = useState(''); // Error message for lookup

  // Loading states
  const [loadingPowers, setLoadingPowers] = useState(false);
  const [powersLoaded, setPowersLoaded] = useState(false); // Track if powers were fetched to avoid re-fetching
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Case number verification state (for verifying user knows the case number)
  const [caseNumberVerificationInput, setCaseNumberVerificationInput] = useState('');
  const [caseNumberVerified, setCaseNumberVerified] = useState(false);
  const [caseNumberVerificationError, setCaseNumberVerificationError] = useState('');

  // Dropdown data
  const [availablePowers, setAvailablePowers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [cities, setCities] = useState([]);

  // Selected replacement power (from inventory) - for One-to-One and Many-to-One
  const [selectedReplacementPower, setSelectedReplacementPower] = useState(null);

  // Multiple replacement powers - for One-to-Many
  const [replacementPowers, setReplacementPowers] = useState([]);
  const [replacementSearches, setReplacementSearches] = useState(['', '']); // Search terms for each replacement
  const [activeReplacementIndex, setActiveReplacementIndex] = useState(0);

  // Per-bond details for One-to-Many (parallel index to replacementPowers).
  // Each entry stores its own bondAmount, executionDate, scAmount + display strings
  // so the agent can enter different values for each replacement bond.
  const [perBondData, setPerBondData] = useState([]);
  const [activeBondIndex, setActiveBondIndex] = useState(0);

  // Form data for details step
  const [formData, setFormData] = useState({
    powerNumber: '',
    bondAmount: '',
    executionDate: new Date(),
    caseNumber: '',
    offense: '',
    executingAgentId: null,
    cityId: null,
    executingState: '',
    defendantFirstName: '',
    defendantLastName: '',
    defendantDob: null,
    defendantSsn: '',
    scAmount: ''
  });

  // Validation
  const [validationResult, setValidationResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState({}); // Non-blocking warnings (e.g., lookup failures)
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Date and currency display states
  const [executionDateText, setExecutionDateText] = useState('');
  const [dobText, setDobText] = useState('');
  const [bondAmountDisplay, setBondAmountDisplay] = useState('');
  const [scAmountDisplay, setScAmountDisplay] = useState('');

  // Search states
  const [powerSearch, setPowerSearch] = useState('');
  const [showPowerDropdown, setShowPowerDropdown] = useState(false);

  // Searchable autocomplete states (#4)
  const [agentSearch, setAgentSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  // Keyboard navigation indices (#4, #16)
  const [highlightedAgentIndex, setHighlightedAgentIndex] = useState(-1);
  const [highlightedCityIndex, setHighlightedCityIndex] = useState(-1);
  const [highlightedPowerIndex, setHighlightedPowerIndex] = useState(-1);
  const [highlightedReplacementPowerIndex, setHighlightedReplacementPowerIndex] = useState(-1);

  // Refs
  const powerDropdownRef = useRef(null);
  const executionDatePickerRef = useRef(null);
  const dobDatePickerRef = useRef(null);
  const agentDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const agentListRef = useRef(null);
  const cityListRef = useRef(null);
  const powerListRef = useRef(null);

  // Derived state
  const isSubmittedReport = reportStatus?.toUpperCase() === 'SUBMITTED';

  // NOTE: Previous implementation used eligiblePowers and filteredEligiblePowers from the current report.
  // The new flow uses manual input where users enter power numbers and the API validates them.
  // Original powers are looked up from executed powers (E, T, F, J, D status) NOT in the current report.

  // Filter replacement powers based on search
  const filteredReplacementPowers = useMemo(() => {
    const powers = Array.isArray(availablePowers) ? availablePowers : [];
    if (!powerSearch) return powers;
    const search = powerSearch.toLowerCase();
    return powers.filter(p =>
      p.powerNumber?.toLowerCase().includes(search) ||
      p.agencyName?.toLowerCase().includes(search)
    );
  }, [availablePowers, powerSearch]);

  const filteredAgents = useMemo(() => {
    if (!agentSearch) return agents;
    const search = agentSearch.toLowerCase();
    return agents.filter(a =>
      `${a.firstName} ${a.lastName}`.toLowerCase().includes(search)
    );
  }, [agents, agentSearch]);

  const filteredCities = useMemo(() => {
    if (!citySearch) return cities;
    const search = citySearch.toLowerCase();
    return cities.filter(c =>
      `${c.name}, ${c.stateCode}`.toLowerCase().startsWith(search)
    );
  }, [cities, citySearch]);

  // Agent and city maps for lookups (by ID and by name)
  const agentMap = useMemo(() => {
    const map = new Map();
    agents.forEach(agent => {
      map.set(agent.executingAgentId, agent);
      // Also key by name for lookup when only names are available
      if (agent.firstName && agent.lastName) {
        map.set(`${agent.firstName.toLowerCase()}_${agent.lastName.toLowerCase()}`, agent);
      }
    });
    return map;
  }, [agents]);

  const cityMap = useMemo(() => {
    const map = new Map();
    cities.forEach(city => {
      map.set(city.cityId, city);
      // Also key by normalized name/state for fuzzy lookup (handles "mc kinney" vs "mckinney")
      if (city.name && city.stateCode) {
        map.set(`${normalizeForLookup(city.name)}_${normalizeForLookup(city.stateCode)}`, city);
      }
    });
    return map;
  }, [cities]);

  // Get type config for current selection
  const currentTypeConfig = rewriteType ? REWRITE_TYPE_CONFIG[rewriteType] : null;

  // Check if the user has already selected the maximum number of original powers for this rewrite type
  const isAtMaxOriginals = currentTypeConfig?.maxOriginals && selectedOriginalPowers.length >= currentTypeConfig.maxOriginals;

  // Check if any selected original power has F/J status (requires case number validation)
  const hasForfeitureOrJudgment = useMemo(() => {
    return selectedOriginalPowers.some(p =>
      CASE_NUMBER_REQUIRED_STATUSES.includes(p.status?.toUpperCase())
    );
  }, [selectedOriginalPowers]);

  // Case verification is required only when at least one original power is in F (Forfeiture)
  // or J (Judgment) status. Case number is optional for all other statuses (E, T, D, U, V),
  // even when a value happens to be on file.
  const requiresCaseVerification = useMemo(() => {
    return selectedOriginalPowers.some(p =>
      CASE_NUMBER_REQUIRED_STATUSES.includes(p.status?.toUpperCase())
    );
  }, [selectedOriginalPowers]);

  // Check if any original should skip auto-discharge:
  // - Judgment (J): always skip, regardless of state
  // - Forfeiture (F): skip only in NC/TX
  const hasSpecialDischargeState = useMemo(() => {
    return selectedOriginalPowers.some(p => {
      const status = p.status?.toUpperCase();
      if (status === 'J') return true;
      if (status === 'F') return SPECIAL_DISCHARGE_STATES.includes(p.executingState?.toUpperCase());
      return false;
    });
  }, [selectedOriginalPowers]);

  // Check if any original has a transfer bond (would need backend flag)
  const hasTransferBond = useMemo(() => {
    return selectedOriginalPowers.some(p => p.hasTransferBond);
  }, [selectedOriginalPowers]);

  // Reset all state
  const resetState = useCallback(() => {
    setCurrentStep(REWRITE_STEPS.SELECT_TYPE);
    setRewriteType(null);
    setSelectedOriginalPowers([]);
    setOriginalPowerInput('');
    setLookingUpOriginal(false);
    setOriginalLookupError('');
    setCaseNumberVerificationInput('');
    setCaseNumberVerified(false);
    setCaseNumberVerificationError('');
    setSelectedReplacementPower(null);
    setReplacementPowers([]);
    setReplacementSearches(['', '']);
    setActiveReplacementIndex(0);
    setPerBondData([]);
    setActiveBondIndex(0);
    setFormData({
      powerNumber: '',
      bondAmount: '', // Not prepopulated per new requirements
      executionDate: new Date(),
      caseNumber: '',
      offense: '',
      executingAgentId: null,
      cityId: null,
      executingState: '',
      defendantFirstName: '',
      defendantLastName: '',
      defendantDob: null,
      defendantSsn: '',
      scAmount: ''
    });
    setValidationResult(null);
    setErrors({});
    setWarnings({});
    setPowerSearch('');
    setShowPowerDropdown(false);
    setExecutionDateText('');
    setDobText('');
    setBondAmountDisplay('');
    setScAmountDisplay('');
    setAvailablePowers([]);
    setPowersLoaded(false);
    setShowCloseConfirm(false);
    setAgentSearch('');
    setCitySearch('');
    setShowAgentDropdown(false);
    setShowCityDropdown(false);
    setHighlightedAgentIndex(-1);
    setHighlightedCityIndex(-1);
    setHighlightedPowerIndex(-1);
    setHighlightedReplacementPowerIndex(-1);
  }, []);

  // Refs for stable handleClose callback (rerender-functional-setstate)
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;
  const originalPowersCountRef = useRef(0);
  originalPowersCountRef.current = selectedOriginalPowers.length;

  const handleClose = useCallback(() => {
    const hasProgress = currentStepRef.current > REWRITE_STEPS.SELECT_TYPE || originalPowersCountRef.current > 0;
    if (hasProgress) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  }, [onClose]);

  // Look up an original power by number for rewrite
  // Uses the backend API to validate and get power details
  const lookupOriginalPower = useCallback(async (powerNumber) => {
    if (!powerNumber?.trim()) return;

    const trimmedNumber = powerNumber.trim().toUpperCase();

    // Validate power number format (should be 9 digits)
    if (!/^\d{9}$/.test(trimmedNumber)) {
      setOriginalLookupError('Power number must be exactly 9 digits');
      return;
    }

    // Check if already added
    if (selectedOriginalPowers.some(p => p.powerNumber?.toUpperCase() === trimmedNumber)) {
      setOriginalLookupError('This power has already been added');
      return;
    }

    setLookingUpOriginal(true);
    setOriginalLookupError('');

    // Look up executed power data via API
    try {
      const result = await lookupExecutedPowerForRewrite(agencyId, trimmedNumber, reportId);

      if (!result.success) {
        setOriginalLookupError(result.error || 'Power not found or not eligible for rewrite');
        setLookingUpOriginal(false);
        return;
      }

      const powerData = result.data;

      // Check if power is in current report (shouldn't be for submitted report rewrites)
      if (powerData.isInCurrentReport) {
        setOriginalLookupError('This power is already in the current report');
        setLookingUpOriginal(false);
        return;
      }

      // Validate defendant matches for many-to-one (must be same defendant)
      if (rewriteType === REWRITE_TYPES.MANY_TO_ONE && selectedOriginalPowers.length > 0) {
        const firstDefendant = selectedOriginalPowers[0];
        const defendantFirstName = (firstDefendant.defendantFirstName || '').trim().toLowerCase();
        const defendantLastName = (firstDefendant.defendantLastName || '').trim().toLowerCase();
        const newFirstName = (powerData.power?.defendantFirstName || '').trim().toLowerCase();
        const newLastName = (powerData.power?.defendantLastName || '').trim().toLowerCase();

        if (newFirstName !== defendantFirstName || newLastName !== defendantLastName) {
          setOriginalLookupError(`Many-to-One requires same defendant. Expected: ${firstDefendant.defendantFirstName} ${firstDefendant.defendantLastName}`);
          setLookingUpOriginal(false);
          return;
        }
      }

      // Add to selected originals with all the data from the API
      const hasHistoricalData = powerData.hasHistoricalData ?? false;
      console.log(`[RewritePower] Power ${trimmedNumber} lookup - status: ${powerData.status}, power.status: ${powerData.power?.status}`);
      const powerToAdd = {
        powerNumber: powerData.power?.powerNumber || trimmedNumber,
        status: powerData.status || powerData.power?.status || 'E',
        defendantFirstName: powerData.power?.defendantFirstName || '',
        defendantLastName: powerData.power?.defendantLastName || '',
        executionDate: powerData.power?.executionDate,
        caseNumber: powerData.power?.caseNumber || '',
        // For F/J powers the backend may have multiple valid case numbers on file
        // (CourtInfo.CaseNumber, CourtInfo.PreviousCaseNumber, and the historical
        // PowerReportPower.CaseNumber). Accept a match against any of them so the
        // user can type what they see in PowersDetailView. Fall back to the single
        // caseNumber for servers that haven't deployed AcceptableCaseNumbers yet.
        acceptableCaseNumbers: Array.isArray(powerData.power?.acceptableCaseNumbers) && powerData.power.acceptableCaseNumbers.length > 0
          ? powerData.power.acceptableCaseNumbers
          : (powerData.power?.caseNumber ? [powerData.power.caseNumber] : []),
        powerAmount: powerData.power?.powerAmount || powerData.power?.bondAmount || 0,
        bondAmount: powerData.power?.bondAmount || powerData.power?.powerAmount || 0,
        executingAgentId: powerData.power?.executingAgentId,
        executingAgentFirstName: powerData.power?.executingAgentFirstName || '',
        executingAgentLastName: powerData.power?.executingAgentLastName || '',
        cityId: powerData.power?.cityId,
        cityName: powerData.power?.cityName || '',
        stateCode: powerData.power?.stateCode,
        offense: powerData.power?.offense,
        defendantDob: powerData.power?.defendantDob,
        defendantSsn: powerData.power?.defendantSsn,
        scAmount: powerData.power?.scAmount,
        premium: powerData.power?.premium || 0,
        net: powerData.power?.net || 0,
        buf: powerData.power?.buf || 0,
        hasTransferBond: powerData.hasTransferBond || false,
        submittedReportId: powerData.power?.submittedReportId,
        submittedDate: powerData.power?.submittedDate,
        _isManualEntry: false, // This was looked up via API
        _hasHistoricalData: hasHistoricalData // Track if historical data was found
      };

      // Block powers without execution record (defense-in-depth)
      if (!hasHistoricalData) {
        setOriginalLookupError('This power does not have an execution record. The original power must be executed before it can be rewritten.');
        setLookingUpOriginal(false);
        return;
      }

      setSelectedOriginalPowers(prev => [...prev, powerToAdd]);
      setOriginalPowerInput('');
      setOriginalLookupError('');

      const defendantName = powerToAdd.defendantFirstName && powerToAdd.defendantLastName
        ? ` - ${powerToAdd.defendantFirstName} ${powerToAdd.defendantLastName}`
        : '';
      toast.success(`Power ${trimmedNumber} added${defendantName}`);

    } catch (error) {
      console.error('Power lookup error:', error);
      setOriginalLookupError('Failed to look up power: ' + (error.message || 'Network error'));
    } finally {
      setLookingUpOriginal(false);
    }
  }, [agencyId, reportId, rewriteType, selectedOriginalPowers]);

  // Remove an original power from selection
  const removeOriginalPower = useCallback((powerNumber) => {
    setSelectedOriginalPowers(prev => prev.filter(p => p.powerNumber !== powerNumber));
    setOriginalLookupError('');
    // Reset case number verification when power is removed
    setCaseNumberVerified(false);
    setCaseNumberVerificationInput('');
    setCaseNumberVerificationError('');
  }, []);

  // Handle case number verification
  const handleVerifyCaseNumber = useCallback(() => {
    const trimmedInput = (caseNumberVerificationInput || '').trim().toUpperCase();

    // For Many-to-One: case number must match AT LEAST ONE of the original powers
    // For One-to-One/One-to-Many: case number must match the single original power
    const isManyToOne = rewriteType === REWRITE_TYPES.MANY_TO_ONE;

    // Check if any selected power is in F/J status (requires case number regardless)
    const hasFJ = selectedOriginalPowers.some(p =>
      CASE_NUMBER_REQUIRED_STATUSES.includes(p.status?.toUpperCase())
    );

    // Build the pool of acceptable case numbers for a single power. For F/J originals the
    // backend may hand back multiple valid values (displayed CaseNumber, CourtInfo.CaseNumber,
    // CourtInfo.PreviousCaseNumber, historical PowerReportPower.CaseNumber) — any should match.
    const getAcceptable = (power) => {
      const list = Array.isArray(power?.acceptableCaseNumbers) && power.acceptableCaseNumbers.length > 0
        ? power.acceptableCaseNumbers
        : [power?.caseNumber];
      return list
        .map(cn => (cn || '').trim().toUpperCase())
        .filter(cn => cn.length > 0);
    };

    if (isManyToOne) {
      // Flatten acceptable case numbers across all selected powers
      const originalCaseNumbers = selectedOriginalPowers.flatMap(getAcceptable);

      // If no powers have case numbers AND no F/J powers, auto-verify (nothing to verify against)
      if (originalCaseNumbers.length === 0 && !hasFJ) {
        setCaseNumberVerified(true);
        setCaseNumberVerificationError('');
        return;
      }

      // User must enter something
      if (!trimmedInput) {
        setCaseNumberVerificationError('Please enter a case number');
        return;
      }

      // If there are case numbers on file, input must match at least one
      if (originalCaseNumbers.length > 0) {
        const matchesAny = originalCaseNumbers.includes(trimmedInput);

        if (matchesAny) {
          setCaseNumberVerified(true);
          setCaseNumberVerificationError('');
        } else {
          setCaseNumberVerificationError('Case number does not match any of the selected powers. If you believe this is an error, reach out to Bankers.');
          setCaseNumberVerified(false);
        }
      } else {
        // F/J powers with no case number on file — accept any non-empty input
        setCaseNumberVerified(true);
        setCaseNumberVerificationError('');
      }
    } else {
      // One-to-One or One-to-Many: must match the single original power's case number(s)
      const acceptable = getAcceptable(selectedOriginalPowers[0]);

      // If original power has no case number AND not F/J, auto-verify (nothing to verify against)
      if (acceptable.length === 0 && !hasFJ) {
        setCaseNumberVerified(true);
        setCaseNumberVerificationError('');
        return;
      }

      // User must enter something
      if (!trimmedInput) {
        setCaseNumberVerificationError('Please enter a case number');
        return;
      }

      // If there's a case number on file, input must match one of the acceptable values
      if (acceptable.length > 0) {
        if (acceptable.includes(trimmedInput)) {
          setCaseNumberVerified(true);
          setCaseNumberVerificationError('');
        } else {
          setCaseNumberVerificationError('Case number does not match. If you believe this is an error, reach out to Bankers.');
          setCaseNumberVerified(false);
        }
      } else {
        // F/J power with no case number on file — accept any non-empty input
        setCaseNumberVerified(true);
        setCaseNumberVerificationError('');
      }
    }
  }, [caseNumberVerificationInput, selectedOriginalPowers, rewriteType]);

  // Initialize when modal opens
  useEffect(() => {
    if (!isOpen) {
      resetState();
      return;
    }

    // Use preloaded data or cached data
    if (preloadedAgents.length > 0) {
      setAgents(preloadedAgents);
    } else {
      const cached = getCachedAgents(agencyId);
      if (cached) setAgents(cached);
    }

    if (preloadedCities.length > 0) {
      setCities(preloadedCities);
    } else {
      const cached = getCachedCities(agencyId);
      if (cached) setCities(cached);
    }
  }, [isOpen, preloadedAgents, preloadedCities, agencyId, getCachedAgents, getCachedCities, resetState]);

  // Handle rewrite type selection - resets dependent state (rerender-move-effect-to-event)
  const handleRewriteTypeSelect = useCallback((type) => {
    setRewriteType(type);
    setSelectedOriginalPowers([]);
    setOriginalPowerInput('');
    setOriginalLookupError('');
    setCaseNumberVerificationInput('');
    setCaseNumberVerified(false);
    setCaseNumberVerificationError('');
    setSelectedReplacementPower(null);
    setReplacementPowers([]);
    setReplacementSearches(['', '']);
    setPerBondData([]);
    setActiveBondIndex(0);
    setPowersLoaded(false);
    setPowerSearch('');
    setErrors({});
  }, []);

  // Powers are loaded on-demand when the user types 3+ characters in the search input
  // (no automatic loading on step entry to avoid lag from unfiltered API calls)

  // Debounced API search as user types in the replacement power input
  useEffect(() => {
    if (!isOpen || currentStep !== REWRITE_STEPS.SELECT_REPLACEMENT) return;

    const searchTerm = rewriteType === REWRITE_TYPES.ONE_TO_MANY
      ? replacementSearches[activeReplacementIndex] || ''
      : powerSearch;

    if (searchTerm.length < 3) { setAvailablePowers([]); return; }

    const timer = setTimeout(async () => {
      try {
        setLoadingPowers(true);
        const result = await getAvailablePowersForRewrite(
          reportId,
          selectedOriginalPowers[0]?.powerNumber || null,
          1,
          10,
          searchTerm
        );
        if (result.success) {
          setAvailablePowers(Array.isArray(result.data) ? result.data : []);
          setPowersLoaded(true);
        }
      } catch (error) {
        console.error('Power search error:', error);
      } finally {
        setLoadingPowers(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [isOpen, currentStep, powerSearch, replacementSearches, activeReplacementIndex, rewriteType, reportId, selectedOriginalPowers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track the first selected power number to detect when we need to reload available powers
  // (API excludes the first original power from available replacements)
  const firstOriginalPowerNumber = selectedOriginalPowers[0]?.powerNumber || null;
  const prevFirstPowerRef = useRef(null);

  useEffect(() => {
    // If the first selected power changed, reset the loaded state so powers will reload
    if (prevFirstPowerRef.current !== null && prevFirstPowerRef.current !== firstOriginalPowerNumber) {
      setPowersLoaded(false);
      setSelectedReplacementPower(null);
      setPowerSearch('');
    }
    prevFirstPowerRef.current = firstOriginalPowerNumber;
  }, [firstOriginalPowerNumber]);

  // Pre-fill form when original power(s) are selected
  // Wait for agents and cities to be loaded before pre-filling (like AddEditPowerModal)
  // Handles both One-to-One (single) and Many-to-One (multiple originals)
  useEffect(() => {
    // Must have at least one original power selected AND agents/cities must be loaded
    if (selectedOriginalPowers.length === 0 || agents.length === 0 || cities.length === 0) {
      return;
    }

    // For Many-to-One, use first power's details but sum the amounts
    const primaryPower = selectedOriginalPowers[0];
    // NOTE: Execution date is NOT prepopulated - user must enter manually
    const dob = primaryPower.defendantDob ? new Date(primaryPower.defendantDob) : null;

    // NOTE: Bond amount and SC amount are NOT prepopulated per business requirements
    // User must enter bond amount manually

    // Look up executingAgent - first by ID, then by name using composite key
    let executingAgent = null;
    const newWarnings = {};
    if (primaryPower.executingAgentId && primaryPower.executingAgentId !== 0) {
      executingAgent = agentMap.get(primaryPower.executingAgentId) || null;
    }
    if (!executingAgent && primaryPower.executingAgentFirstName && primaryPower.executingAgentLastName) {
      const nameKey = `${primaryPower.executingAgentFirstName.toLowerCase()}_${primaryPower.executingAgentLastName.toLowerCase()}`;
      executingAgent = agentMap.get(nameKey) || null;
    }
    // Warn if agent was expected but not found
    if (!executingAgent && (primaryPower.executingAgentId || primaryPower.executingAgentFirstName)) {
      const agentName = primaryPower.executingAgentFirstName
        ? `${primaryPower.executingAgentFirstName} ${primaryPower.executingAgentLastName}`
        : `ID ${primaryPower.executingAgentId}`;
      newWarnings.executingAgentId = `Original agent (${agentName}) not found in current list. Please select an agent.`;
      console.warn(`Agent lookup failed for power ${primaryPower.powerNumber}:`, agentName);
    }

    // Look up city - first by ID, then by name/state using composite key
    let city = null;
    const cityIdFromPower = primaryPower.powerReportCityId || primaryPower.cityId;
    if (cityIdFromPower && cityIdFromPower !== 0) {
      city = cityMap.get(cityIdFromPower) || null;
    }
    if (!city && primaryPower.cityName && primaryPower.stateCode) {
      const cityKey = `${normalizeForLookup(primaryPower.cityName)}_${normalizeForLookup(primaryPower.stateCode)}`;
      city = cityMap.get(cityKey) || null;
    }
    // Warn if city was expected but not found
    if (!city && (cityIdFromPower || primaryPower.cityName)) {
      const cityName = primaryPower.cityName
        ? `${primaryPower.cityName}, ${primaryPower.stateCode}`
        : `ID ${cityIdFromPower}`;
      newWarnings.cityId = `Original city (${cityName}) not found in current list. Please select a city.`;
      console.warn(`City lookup failed for power ${primaryPower.powerNumber}:`, cityName);
    }

    setWarnings(newWarnings);

    // For Many-to-One, clear case number and offense since combining multiple cases
    const isManyToOne = selectedOriginalPowers.length > 1;

    setFormData(prev => ({
      ...prev,
      // Bond amount is NOT prepopulated per business requirements - user must enter manually
      bondAmount: '',
      // Execution date is NOT prepopulated - user must enter manually
      executionDate: null,
      // Pre-populate case number from first original power (user can override)
      caseNumber: primaryPower.caseNumber || '',
      offense: isManyToOne ? '' : (primaryPower.offense || '').toUpperCase(),
      executingAgentId: executingAgent?.executingAgentId || null,
      cityId: city?.cityId || null,
      executingState: city?.stateCode || primaryPower.stateCode || '',
      defendantFirstName: primaryPower.defendantFirstName || '',
      defendantLastName: primaryPower.defendantLastName || '',
      defendantDob: dob,
      defendantSsn: primaryPower.defendantSsn ? formatSSN(primaryPower.defendantSsn) : '',
      scAmount: '' // SC Amount also not prepopulated
    }));

    // Execution date display cleared - user enters manually
    setExecutionDateText('');
    setDobText(formatDateForDisplay(dob));
    // Bond amount display cleared - user enters manually
    setBondAmountDisplay('');
    setScAmountDisplay('');

    // Pre-fill autocomplete search text
    if (executingAgent) {
      setAgentSearch(`${executingAgent.firstName} ${executingAgent.lastName}`);
    }
    if (city) {
      setCitySearch(`${toTitleCase(city.name)}, ${city.stateCode}`);
    }
  }, [selectedOriginalPowers, agents.length, cities.length, agentMap, cityMap]);

  // Initialize / sync per-bond data for One-to-Many. Each replacement bond gets its
  // own bondAmount (defaulted to the power's denomination), executionDate, and scAmount
  // so distinct values can be entered for each. Match existing entries by powerNumber
  // (not index) so removing a slot doesn't shift values onto the wrong bond.
  useEffect(() => {
    if (rewriteType !== REWRITE_TYPES.ONE_TO_MANY) return;

    setPerBondData(prev => {
      const next = replacementPowers.map((power) => {
        if (!power) {
          return {
            _powerNumber: null,
            bondAmount: '',
            bondAmountDisplay: '',
            executionDate: null,
            executionDateText: '',
            scAmount: '',
            scAmountDisplay: ''
          };
        }
        const existing = prev.find(b => b?._powerNumber === power.powerNumber);
        if (existing) return existing;
        const denom = power.denomination ?? '';
        const denomStr = denom !== '' && denom !== null && denom !== undefined ? String(denom) : '';
        return {
          _powerNumber: power.powerNumber,
          bondAmount: denomStr,
          bondAmountDisplay: denomStr ? formatCurrencyInput(denomStr) : '',
          executionDate: null,
          executionDateText: '',
          scAmount: '',
          scAmountDisplay: ''
        };
      });
      // Avoid setting state if nothing actually changed (prevents render loops)
      const sameLength = prev.length === next.length;
      const sameContent = sameLength && prev.every((p, i) => p === next[i]);
      return sameContent ? prev : next;
    });

    setActiveBondIndex(prev => {
      const max = Math.max(0, replacementPowers.length - 1);
      return prev > max ? max : prev;
    });
  }, [rewriteType, replacementPowers]);

  // Auto-verify case number when no verification is needed, or reset when powers change.
  // Verification is required when any power has a case number on file OR is F/J status.
  const prevPowersCountRef = useRef(0);

  useEffect(() => {
    if (selectedOriginalPowers.length === 0) {
      prevPowersCountRef.current = 0;
      return;
    }

    // Check if verification is needed: F/J status OR has case number on file
    const hasFJ = selectedOriginalPowers.some(p =>
      CASE_NUMBER_REQUIRED_STATUSES.includes(p.status?.toUpperCase())
    );
    const hasCaseNumbers = selectedOriginalPowers.some(p =>
      (p.caseNumber || '').trim().length > 0
    );
    const needsVerification = hasFJ || hasCaseNumbers;

    // If no verification needed (non-F/J with no case numbers), auto-verify.
    if (!needsVerification) {
      setCaseNumberVerified(true);
      setCaseNumberVerificationError('');
    } else if (selectedOriginalPowers.length !== prevPowersCountRef.current) {
      // Power added/changed — reset verification so user must verify again
      setCaseNumberVerified(false);
      setCaseNumberVerificationInput('');
      setCaseNumberVerificationError('');
    }

    prevPowersCountRef.current = selectedOriginalPowers.length;
  }, [selectedOriginalPowers]);

  // Handle click outside to close dropdowns + capture-phase Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (powerDropdownRef.current && !powerDropdownRef.current.contains(event.target)) {
        setShowPowerDropdown(false);
      }
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(event.target)) {
        setShowAgentDropdown(false);
      }
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showPowerDropdown || showAgentDropdown || showCityDropdown) {
          // Close dropdowns first, don't bubble to modal close
          setShowPowerDropdown(false);
          setShowAgentDropdown(false);
          setShowCityDropdown(false);
          event.stopPropagation();
        } else {
          // No dropdown open — close the modal
          handleClose();
          event.stopPropagation();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [isOpen, showPowerDropdown, showAgentDropdown, showCityDropdown, handleClose]);

  // Scroll highlighted dropdown items into view (consolidated from 3 separate effects)
  useEffect(() => {
    const scrollToHighlighted = (index, listRef) => {
      if (index >= 0 && listRef.current) {
        const item = listRef.current.children[index];
        if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    };
    scrollToHighlighted(highlightedAgentIndex, agentListRef);
    scrollToHighlighted(highlightedCityIndex, cityListRef);
    scrollToHighlighted(highlightedPowerIndex, powerListRef);
  }, [highlightedAgentIndex, highlightedCityIndex, highlightedPowerIndex]);

  // NOTE: handleToggleOriginalPower removed - no longer used since we switched to manual input
  // Original powers are now added via lookupOriginalPower API call and removed via removeOriginalPower

  // Handle replacement power selection
  const handleReplacementPowerSelected = useCallback((power) => {
    setSelectedReplacementPower(power);
    setFormData(prev => ({
      ...prev,
      powerNumber: power.powerNumber
    }));
    setPowerSearch('');
    setShowPowerDropdown(false);
    setErrors(prev => ({ ...prev, powerNumber: null }));
  }, []);

  const handleAgentSelected = useCallback((selectedAgentObj) => {
    if (selectedAgentObj) {
      setFormData(prev => ({ ...prev, executingAgentId: selectedAgentObj.executingAgentId }));
      setAgentSearch(`${selectedAgentObj.firstName} ${selectedAgentObj.lastName}`);
      setErrors(prev => ({ ...prev, executingAgentId: null }));
    } else {
      setFormData(prev => ({ ...prev, executingAgentId: null }));
      setAgentSearch('');
    }
    setShowAgentDropdown(false);
    setHighlightedAgentIndex(-1);
  }, []);

  const handleCitySelected = useCallback((selectedCityObj) => {
    if (selectedCityObj) {
      setFormData(prev => ({
        ...prev,
        cityId: selectedCityObj.cityId,
        executingState: selectedCityObj.stateCode
      }));
      setCitySearch(`${toTitleCase(selectedCityObj.name)}, ${selectedCityObj.stateCode}`);
      setErrors(prev => ({ ...prev, cityId: null }));
    } else {
      setFormData(prev => ({
        ...prev,
        cityId: null,
        executingState: ''
      }));
      setCitySearch('');
    }
    setShowCityDropdown(false);
    setHighlightedCityIndex(-1);
  }, []);

  // Handle One-to-Many replacement power selection
  const handleOneToManyReplacementSelected = useCallback((power, index) => {
    // Prevent selecting the same power in multiple slots
    const alreadyUsed = replacementPowers.some((p, i) => i !== index && p?.powerNumber === power.powerNumber);
    if (alreadyUsed) {
      toast.error(`Power ${power.powerNumber} is already selected in another slot`);
      return;
    }

    setReplacementPowers(prev => {
      const updated = [...prev];
      updated[index] = power;
      return updated;
    });
    setReplacementSearches(prev => {
      const updated = [...prev];
      updated[index] = '';
      return updated;
    });
    setErrors(prev => ({ ...prev, powerNumber: null }));
  }, [replacementPowers]);

  // Add new replacement power slot for One-to-Many
  const handleAddReplacementSlot = useCallback(() => {
    setReplacementPowers(prev => [...prev, null]);
    setReplacementSearches(prev => [...prev, '']);
  }, []);

  // Remove replacement power slot for One-to-Many
  const handleRemoveReplacementSlot = useCallback((index) => {
    if (replacementPowers.length <= 1) return; // Keep at least 1 slot; minimum 2 enforced by validateStep2
    setReplacementPowers(prev => prev.filter((_, i) => i !== index));
    setReplacementSearches(prev => prev.filter((_, i) => i !== index));
  }, [replacementPowers.length]);

  // Update replacement search term for One-to-Many
  const handleReplacementSearchChange = useCallback((value, index) => {
    setReplacementSearches(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  }, []);

  // Keyboard navigation for searchable dropdowns (extracted via useDropdownKeyboard helper)
  const handleAgentKeyDown = useDropdownKeyboard(showAgentDropdown, setShowAgentDropdown, filteredAgents, highlightedAgentIndex, setHighlightedAgentIndex, handleAgentSelected);
  const handleCityKeyDown = useDropdownKeyboard(showCityDropdown, setShowCityDropdown, filteredCities, highlightedCityIndex, setHighlightedCityIndex, handleCitySelected);
  const handleReplacementPowerKeyDown = useDropdownKeyboard(showPowerDropdown, setShowPowerDropdown, filteredReplacementPowers, highlightedPowerIndex, setHighlightedPowerIndex, handleReplacementPowerSelected);

  // One-to-Many replacement slot keyboard handler - uses the helper with slot-specific wiring
  const handleReplacementSlotKeyDown = useCallback((e, filtered, slotIndex) => {
    const isDropdownVisible = activeReplacementIndex === slotIndex && filtered.length > 0;
    if (!isDropdownVisible) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setActiveReplacementIndex(slotIndex);
        setHighlightedReplacementPowerIndex(0);
        e.preventDefault();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedReplacementPowerIndex(prev => prev < filtered.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedReplacementPowerIndex(prev => prev > 0 ? prev - 1 : filtered.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedReplacementPowerIndex >= 0 && highlightedReplacementPowerIndex < filtered.length) {
          handleOneToManyReplacementSelected(filtered[highlightedReplacementPowerIndex], slotIndex);
        }
        break;
      case 'Tab':
        if (highlightedReplacementPowerIndex >= 0 && highlightedReplacementPowerIndex < filtered.length) {
          handleOneToManyReplacementSelected(filtered[highlightedReplacementPowerIndex], slotIndex);
        }
        break;
      case 'Escape':
        setHighlightedReplacementPowerIndex(-1);
        break;
      default:
        break;
    }
  }, [activeReplacementIndex, highlightedReplacementPowerIndex, handleOneToManyReplacementSelected]);

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  // Handle date text changes
  const handleDateTextChange = useCallback((field, value) => {
    const formatted = formatDateInputText(value);
    if (field === 'executionDate') {
      setExecutionDateText(formatted);
    } else if (field === 'defendantDob') {
      setDobText(formatted);
    }
    setErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  // Handle date text blur
  const handleDateTextBlur = useCallback((field) => {
    const textValue = field === 'executionDate' ? executionDateText : dobText;

    if (!textValue) {
      setFormData(prev => ({ ...prev, [field]: null }));
      return;
    }

    const parsedDate = parseDateInput(textValue);
    if (parsedDate) {
      setFormData(prev => ({ ...prev, [field]: parsedDate }));
      const fullDateText = formatDateForDisplay(parsedDate);
      if (field === 'executionDate') {
        setExecutionDateText(fullDateText);
      } else if (field === 'defendantDob') {
        setDobText(fullDateText);
      }
    }
  }, [executionDateText, dobText]);

  // Handle currency input changes
  const handleCurrencyChange = useCallback((field, value, setDisplayFn) => {
    const formatted = formatCurrencyInput(value);
    setDisplayFn(formatted);
    const rawValue = parseCurrencyInput(formatted);
    setFormData(prev => ({ ...prev, [field]: rawValue }));
    setErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  // Per-bond field handlers for One-to-Many (write into perBondData[activeBondIndex])
  const handlePerBondCurrencyChange = useCallback((field, displayField, value) => {
    const formatted = formatCurrencyInput(value);
    const rawValue = parseCurrencyInput(formatted);
    setPerBondData(prev => {
      const updated = [...prev];
      const current = updated[activeBondIndex];
      if (!current) return prev;
      updated[activeBondIndex] = { ...current, [field]: rawValue, [displayField]: formatted };
      return updated;
    });
    setErrors(prev => ({ ...prev, [field]: null }));
  }, [activeBondIndex]);

  const handlePerBondDateTextChange = useCallback((value) => {
    const formatted = formatDateInputText(value);
    setPerBondData(prev => {
      const updated = [...prev];
      const current = updated[activeBondIndex];
      if (!current) return prev;
      updated[activeBondIndex] = { ...current, executionDateText: formatted };
      return updated;
    });
    setErrors(prev => ({ ...prev, executionDate: null }));
  }, [activeBondIndex]);

  const handlePerBondDateBlur = useCallback(() => {
    setPerBondData(prev => {
      const updated = [...prev];
      const current = updated[activeBondIndex];
      if (!current) return prev;
      const text = current.executionDateText || '';
      if (!text) {
        updated[activeBondIndex] = { ...current, executionDate: null };
        return updated;
      }
      const parsed = parseDateInput(text);
      if (parsed) {
        updated[activeBondIndex] = {
          ...current,
          executionDate: parsed,
          executionDateText: formatDateForDisplay(parsed)
        };
      }
      return updated;
    });
  }, [activeBondIndex]);

  const handlePerBondDatePickerChange = useCallback((dateValue) => {
    setPerBondData(prev => {
      const updated = [...prev];
      const current = updated[activeBondIndex];
      if (!current) return prev;
      updated[activeBondIndex] = {
        ...current,
        executionDate: dateValue,
        executionDateText: formatDateForDisplay(dateValue)
      };
      return updated;
    });
    setErrors(prev => ({ ...prev, executionDate: null }));
  }, [activeBondIndex]);

  // Validate step 0 (type selection)
  const validateStep0 = useCallback(() => {
    if (!rewriteType) {
      setErrors({ rewriteType: 'Please select a rewrite type' });
      return false;
    }
    return true;
  }, [rewriteType]);

  // Validate step 1 (original power selection)
  const validateStep1 = useCallback(() => {
    const config = REWRITE_TYPE_CONFIG[rewriteType];
    if (!config) return false;

    if (selectedOriginalPowers.length < config.minOriginals) {
      setErrors({ originalPower: `Please select at least ${config.minOriginals} power(s) to replace` });
      return false;
    }

    if (config.maxOriginals && selectedOriginalPowers.length > config.maxOriginals) {
      setErrors({ originalPower: `Maximum ${config.maxOriginals} power(s) allowed for ${config.label}` });
      return false;
    }

    // Verify all selected powers have execution records (defense-in-depth)
    const hasUnexecutedPower = selectedOriginalPowers.some(p => p._hasHistoricalData === false);
    if (hasUnexecutedPower) {
      setErrors({ originalPower: 'One or more selected powers do not have an execution record. Remove them and select only executed powers.' });
      return false;
    }

    // Require case number verification before proceeding (F/J powers or powers with case number on file)
    if (requiresCaseVerification && !caseNumberVerified) {
      setErrors({ originalPower: 'Please verify the case number before proceeding' });
      return false;
    }

    return true;
  }, [rewriteType, selectedOriginalPowers, caseNumberVerified, requiresCaseVerification]);

  // Validate step 2 (replacement power selection)
  const validateStep2 = useCallback(() => {
    const isOneToMany = rewriteType === REWRITE_TYPES.ONE_TO_MANY;

    if (isOneToMany) {
      // One-to-Many requires at least 2 replacement powers
      if (replacementPowers.length < 2) {
        setErrors({ powerNumber: 'One-to-Many requires at least 2 replacement powers' });
        return false;
      }
      // Check that all replacement powers are valid (have power numbers)
      const invalidIndex = replacementPowers.findIndex(p => !p || !p.powerNumber);
      if (invalidIndex >= 0) {
        setErrors({ powerNumber: `Replacement #${invalidIndex + 1} is not selected` });
        return false;
      }
      return true;
    }

    // One-to-One and Many-to-One: single replacement power
    if (!selectedReplacementPower) {
      // Try to find an exact match based on what the user typed
      if (powerSearch && availablePowers.length > 0) {
        const exactMatch = availablePowers.find(
          p => p.powerNumber?.toLowerCase() === powerSearch.toLowerCase().trim()
        );
        if (exactMatch) {
          setSelectedReplacementPower(exactMatch);
          setFormData(prev => ({
            ...prev,
            powerNumber: exactMatch.powerNumber
          }));
          setErrors({});
          return true;
        }
      }

      if (powerSearch && powerSearch.trim()) {
        setErrors({ powerNumber: 'No available power found matching "' + powerSearch.trim() + '"' });
      } else {
        setErrors({ powerNumber: 'Please select a replacement power' });
      }
      return false;
    }
    return true;
  }, [selectedReplacementPower, powerSearch, availablePowers, rewriteType, replacementPowers]);

  // Validate the bond-amount and execution-date for a single bond payload.
  // Used both per-bond on the One-to-Many sub-stepper and against formData for
  // One-to-One / Many-to-One. Returns just the errors map; caller decides scope.
  const validateBondPayload = useCallback((bondAmountValue, executionDateValue) => {
    const newErrors = {};

    if (!bondAmountValue || parseFloat(bondAmountValue) <= 0) {
      newErrors.bondAmount = 'Bond amount must be greater than 0';
    }

    if (!executionDateValue) {
      newErrors.executionDate = 'Execution date is required';
    } else {
      const execDate = new Date(executionDateValue);
      const now = new Date();
      if (execDate > now) {
        newErrors.executionDate = 'Execution date cannot be in the future';
      }
      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - VALIDATION_RULES.MAX_EXECUTION_DATE_YEARS_BACK);
      if (execDate < minDate) {
        newErrors.executionDate = `Execution date cannot be more than ${VALIDATION_RULES.MAX_EXECUTION_DATE_YEARS_BACK} years ago`;
      }

      if (!newErrors.executionDate && selectedOriginalPowers.length > 0) {
        const latestOriginalDate = selectedOriginalPowers.reduce((latest, p) => {
          if (!p.executionDate) return latest;
          const d = new Date(p.executionDate);
          return (!latest || d > latest) ? d : latest;
        }, null);

        if (latestOriginalDate) {
          const execDateOnly = new Date(execDate.getFullYear(), execDate.getMonth(), execDate.getDate());
          const origDateOnly = new Date(latestOriginalDate.getFullYear(), latestOriginalDate.getMonth(), latestOriginalDate.getDate());
          if (execDateOnly < origDateOnly) {
            newErrors.executionDate = `Rewrite execution date cannot be prior to the original execution date (${formatDateForDisplay(latestOriginalDate)})`;
          }
        }
      }
    }

    return newErrors;
  }, [selectedOriginalPowers]);

  // Validate fields shared across all bonds (defendant, agent, city, SSN, etc.)
  const validateSharedFields = useCallback(() => {
    const newErrors = {};

    if (!formData.defendantFirstName) {
      newErrors.defendantFirstName = 'Defendant first name is required';
    }

    if (!formData.defendantLastName) {
      newErrors.defendantLastName = 'Defendant last name is required';
    }

    if (!formData.executingAgentId) {
      newErrors.executingAgentId = 'Executing agent is required';
    }

    if (!formData.cityId) {
      newErrors.cityId = 'Court city is required';
    }

    if (formData.defendantSsn) {
      if (!VALIDATION_RULES.SSN_PATTERN.test(formData.defendantSsn)) {
        newErrors.defendantSsn = 'SSN must be in format XXX-XX-XXXX';
      } else {
        const [area, group, serial] = formData.defendantSsn.split('-');
        const areaNum = parseInt(area, 10);
        const groupNum = parseInt(group, 10);
        const serialNum = parseInt(serial, 10);

        if (areaNum === 0 || areaNum === 666 || areaNum >= 900) {
          newErrors.defendantSsn = 'Invalid SSN: Area number (first 3 digits) is not valid';
        } else if (groupNum === 0) {
          newErrors.defendantSsn = 'Invalid SSN: Group number (middle 2 digits) cannot be 00';
        } else if (serialNum === 0) {
          newErrors.defendantSsn = 'Invalid SSN: Serial number (last 4 digits) cannot be 0000';
        }
      }
    }

    return newErrors;
  }, [formData]);

  // Validate the currently active bond on the One-to-Many sub-stepper. Sets
  // bondAmount/executionDate errors on the shared errors object (which the
  // currently-rendered bond reads from).
  const validateActiveBond = useCallback(() => {
    const bond = perBondData[activeBondIndex];
    const bondErrors = validateBondPayload(bond?.bondAmount, bond?.executionDate);
    setErrors(prev => ({ ...prev, ...bondErrors, bondAmount: bondErrors.bondAmount || null, executionDate: bondErrors.executionDate || null }));
    return Object.keys(bondErrors).length === 0;
  }, [perBondData, activeBondIndex, validateBondPayload]);

  // Validate step 3 (details) — full validation across shared fields and (for
  // One-to-Many) every per-bond entry. Jumps the sub-stepper to the first
  // failing bond if any per-bond errors are found.
  const validateStep3 = useCallback(() => {
    const isOneToMany = rewriteType === REWRITE_TYPES.ONE_TO_MANY;
    let newErrors = validateSharedFields();

    if (isOneToMany) {
      let firstFailingIndex = -1;
      let firstFailingErrors = null;
      for (let i = 0; i < perBondData.length; i++) {
        const bond = perBondData[i];
        const bondErrors = validateBondPayload(bond?.bondAmount, bond?.executionDate);
        if (Object.keys(bondErrors).length > 0 && firstFailingIndex === -1) {
          firstFailingIndex = i;
          firstFailingErrors = bondErrors;
        }
      }
      if (firstFailingIndex !== -1) {
        setActiveBondIndex(firstFailingIndex);
        newErrors = { ...newErrors, ...firstFailingErrors };
      }
    } else {
      const bondErrors = validateBondPayload(formData.bondAmount, formData.executionDate);
      newErrors = { ...newErrors, ...bondErrors };
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [rewriteType, perBondData, formData.bondAmount, formData.executionDate, validateBondPayload, validateSharedFields]);

  // Validate rewrite with backend
  const validateRewriteWithBackend = useCallback(async () => {
    if (!isSubmittedReport) return true;

    try {
      setValidating(true);

      const isOneToMany = rewriteType === REWRITE_TYPES.ONE_TO_MANY;
      const buildSharedReplacementFields = () => ({
        caseNumber: formData.caseNumber || null,
        offense: formData.offense?.toUpperCase() || null,
        executingAgentId: formData.executingAgentId,
        cityId: formData.cityId,
        cityName: selectedCity?.name || '',
        executingState: formData.executingState,
        defendantFirstName: formData.defendantFirstName,
        defendantLastName: formData.defendantLastName,
        defendantDob: formatDateForInput(formData.defendantDob) || null,
        defendantSsn: formData.defendantSsn || null
      });

      const replacementPayload = isOneToMany
        ? replacementPowers.filter(p => p).map(power => {
            const bond = perBondData.find(b => b?._powerNumber === power.powerNumber);
            return {
              powerNumber: power.powerNumber,
              bondAmount: parseFloat(bond?.bondAmount) || 0,
              executionDate: formatDateForInput(bond?.executionDate),
              ...buildSharedReplacementFields(),
              scAmount: bond?.scAmount ? parseFloat(bond.scAmount) : null
            };
          })
        : [{
            powerNumber: selectedReplacementPower.powerNumber,
            bondAmount: parseFloat(formData.bondAmount) || 0,
            executionDate: formatDateForInput(formData.executionDate),
            ...buildSharedReplacementFields(),
            scAmount: formData.scAmount ? parseFloat(formData.scAmount) : null
          }];

      const validationData = {
        originalPowerNumbers: selectedOriginalPowers.map(p => p.powerNumber),
        replacementPowers: replacementPayload
      };

      const result = await validateRewrite(reportId, validationData);

      if (result.success) {
        setValidationResult(result.data);
        return result.data.isValid;
      } else {
        toast.error('Validation failed: ' + (result.error || 'Unknown error'));
        return false;
      }
    } catch (error) {
      toast.error('Validation failed: ' + error.message);
      return false;
    } finally {
      setValidating(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmittedReport, reportId, rewriteType, selectedOriginalPowers, selectedReplacementPower, replacementPowers, perBondData, formData]);

  // Handle next step
  const handleNextStep = useCallback(async () => {
    if (currentStep === REWRITE_STEPS.SELECT_TYPE) {
      if (!validateStep0()) return;
      setCurrentStep(REWRITE_STEPS.SELECT_ORIGINALS);
    } else if (currentStep === REWRITE_STEPS.SELECT_ORIGINALS) {
      if (!validateStep1()) return;
      setCurrentStep(REWRITE_STEPS.SELECT_REPLACEMENT);
    } else if (currentStep === REWRITE_STEPS.SELECT_REPLACEMENT) {
      if (!validateStep2()) return;
      setCurrentStep(REWRITE_STEPS.ENTER_DETAILS);
    } else if (currentStep === REWRITE_STEPS.ENTER_DETAILS) {
      const isOneToMany = rewriteType === REWRITE_TYPES.ONE_TO_MANY;
      const totalBonds = perBondData.length;

      // One-to-Many sub-stepper: walk through each bond before advancing to Review.
      if (isOneToMany && activeBondIndex < totalBonds - 1) {
        if (!validateActiveBond()) return;
        setActiveBondIndex(activeBondIndex + 1);
        return;
      }

      if (!validateStep3()) return;

      // For submitted reports, validate with backend before proceeding
      if (isSubmittedReport && currentTypeConfig?.supported) {
        const isValid = await validateRewriteWithBackend();
        if (!isValid) return;
      }

      setCurrentStep(REWRITE_STEPS.REVIEW);
    }
  }, [currentStep, rewriteType, perBondData.length, activeBondIndex, validateActiveBond, validateStep0, validateStep1, validateStep2, validateStep3, isSubmittedReport, currentTypeConfig, validateRewriteWithBackend]);

  // Handle previous step
  const handlePreviousStep = useCallback(() => {
    if (currentStep === REWRITE_STEPS.SELECT_ORIGINALS) {
      // Going back to type selection - reset the type so user can re-select
      setRewriteType(null);
      setCurrentStep(REWRITE_STEPS.SELECT_TYPE);
    } else if (currentStep === REWRITE_STEPS.SELECT_REPLACEMENT) {
      setCurrentStep(REWRITE_STEPS.SELECT_ORIGINALS);
    } else if (currentStep === REWRITE_STEPS.ENTER_DETAILS) {
      // One-to-Many sub-stepper: walk back through bonds before leaving Details.
      if (rewriteType === REWRITE_TYPES.ONE_TO_MANY && activeBondIndex > 0) {
        setActiveBondIndex(activeBondIndex - 1);
        return;
      }
      setCurrentStep(REWRITE_STEPS.SELECT_REPLACEMENT);
    } else if (currentStep === REWRITE_STEPS.REVIEW) {
      setCurrentStep(REWRITE_STEPS.ENTER_DETAILS);
    }
  }, [currentStep, rewriteType, activeBondIndex]);

  // Execute rewrite
  // BACKEND REQUIREMENTS (to be implemented on the backend):
  // 1. NC/TX States: For Forfeiture (F) or Judgment (J) status powers in NC or TX,
  //    the original power should NOT be automatically discharged. This is a special
  //    case handled by these states' bail bond regulations.
  // 2. Transfer Bond Handling: If the original power has an associated Transfer Bond
  //    (hasTransferBond flag), the backend needs to:
  //    - Transfer the transfer bond association to the new power
  //    - Update the transfer bond records accordingly
  //    - Ensure the transfer bond premium/commission is properly handled
  // 3. F/J Case Number Validation: For Forfeiture/Judgment powers, the case number
  //    on the replacement must match the original. This is validated on frontend
  //    but should also be enforced on backend.
  const executeRewrite = useCallback(async () => {
    // Check if type is supported
    if (!currentTypeConfig?.supported) {
      toast.error(`${currentTypeConfig?.label} rewrites are not yet supported. Please use One-to-One rewrites.`);
      return;
    }

    try {
      setSubmitting(true);

      let result;
      const isManyToOne = rewriteType === REWRITE_TYPES.MANY_TO_ONE;
      const isOneToMany = rewriteType === REWRITE_TYPES.ONE_TO_MANY;

      // Use submitted rewrite flow for all cases - it works with power numbers
      // and doesn't require an existing PowerReportPowerId
      let rewriteData;

      if (isOneToMany) {
          // One-to-Many: each replacement bond has its own user-entered amount,
          // execution date, and SC premium from perBondData. Shared fields
          // (defendant, agent, city, case, offense) come from formData.
          const validReplacementPowers = replacementPowers.filter(p => p);

          rewriteData = {
            originalPowerNumbers: selectedOriginalPowers.map(p => p.powerNumber),
            replacementPowers: validReplacementPowers.map((power) => {
              const bond = perBondData.find(b => b?._powerNumber === power.powerNumber);
              return {
                powerNumber: power.powerNumber,
                bondAmount: parseFloat(bond?.bondAmount) || 0,
                executionDate: formatDateForInput(bond?.executionDate),
                caseNumber: formData.caseNumber || null,
                offense: formData.offense?.toUpperCase() || null,
                executingAgentId: formData.executingAgentId,
                cityId: formData.cityId,
                cityName: selectedCity?.name || '',
                executingState: formData.executingState,
                defendantFirstName: formData.defendantFirstName,
                defendantLastName: formData.defendantLastName,
                defendantDob: formatDateForInput(formData.defendantDob) || null,
                defendantSsn: formData.defendantSsn || null,
                scAmount: bond?.scAmount ? parseFloat(bond.scAmount) : null
              };
            }),
            rewriteReason: `One-to-Many rewrite: Split into ${validReplacementPowers.length} powers`
          };
        } else {
          // One-to-One and Many-to-One: Single replacement power
          rewriteData = {
            originalPowerNumbers: selectedOriginalPowers.map(p => p.powerNumber),
            replacementPowers: [{
              powerNumber: selectedReplacementPower.powerNumber,
              bondAmount: parseFloat(formData.bondAmount) || 0,
              executionDate: formatDateForInput(formData.executionDate),
              caseNumber: formData.caseNumber || null,
              offense: formData.offense?.toUpperCase() || null,
              executingAgentId: formData.executingAgentId,
              cityId: formData.cityId,
              cityName: selectedCity?.name || '',
              executingState: formData.executingState,
              defendantFirstName: formData.defendantFirstName,
              defendantLastName: formData.defendantLastName,
              defendantDob: formatDateForInput(formData.defendantDob) || null,
              defendantSsn: formData.defendantSsn || null,
              scAmount: formData.scAmount ? parseFloat(formData.scAmount) : null
            }],
            rewriteReason: isManyToOne
              ? `Many-to-One rewrite: Combined ${selectedOriginalPowers.length} powers`
              : 'Rewrite from external portal',
            // Additional flags for backend processing
            hasForfeitureOrJudgment: hasForfeitureOrJudgment, // Backend should validate case number matching
            hasSpecialDischargeState: hasSpecialDischargeState, // NC/TX: don't discharge original automatically
            hasTransferBond: hasTransferBond // Backend should handle transfer bond association
          };
        }

      result = await executeSubmittedRewrite(reportId, rewriteData);

      if (result.success) {
        let successMessage;
        if (isOneToMany) {
          successMessage = `Successfully split power into ${replacementPowers.filter(p => p).length} new powers`;
        } else if (isManyToOne) {
          successMessage = `Successfully combined ${selectedOriginalPowers.length} powers into one`;
        } else {
          successMessage = 'Power rewritten successfully';
        }
        toast.success(successMessage);
        onSuccess && onSuccess(result.data);
        onClose();
      } else {
        toast.error('Rewrite failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Rewrite failed: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, selectedOriginalPowers, selectedReplacementPower, formData, currentTypeConfig, rewriteType, replacementPowers, perBondData, onSuccess, onClose, hasForfeitureOrJudgment, hasSpecialDischargeState, hasTransferBond]);

  // Get selected agent and city names (memoized to avoid recalculating on every render)
  const selectedAgent = useMemo(() =>
    formData.executingAgentId ? agentMap.get(formData.executingAgentId) : null,
    [formData.executingAgentId, agentMap]
  );

  const selectedCity = useMemo(() =>
    formData.cityId ? cityMap.get(formData.cityId) : null,
    [formData.cityId, cityMap]
  );

  // Calculate total amount for selected originals
  const totalOriginalAmount = useMemo(() => {
    return selectedOriginalPowers.reduce((sum, p) => sum + (parseFloat(p.powerAmount) || 0), 0);
  }, [selectedOriginalPowers]);

  // Render step indicator - mobile-friendly with hidden labels on small screens
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-2 px-2">
      {STEP_CONFIG.map((item, index) => {
        const isCompleted = currentStep > item.step;
        const isCurrent = currentStep === item.step;
        const StepElement = isCompleted ? 'button' : 'div';

        return (
          <div key={item.step} className="flex items-center flex-shrink-0">
            <StepElement
              {...(isCompleted ? {
                type: 'button',
                onClick: () => setCurrentStep(item.step),
                'aria-label': `Go back to ${item.label} step`,
              } : {})}
              title={item.label}
              className={`flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full text-xs font-medium transition-colors ${
                isCompleted ? 'cursor-pointer hover:ring-2 hover:ring-green-300 dark:hover:ring-green-600' : ''
              } ${
                isCurrent
                  ? 'bg-primary text-white'
                  : isCompleted
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
            >
              {isCompleted ? (
                <CheckCircleIcon className="h-4 w-4" />
              ) : (
                index + 1
              )}
            </StepElement>
            {/* Hide labels on mobile, show on sm+ */}
            <span className={`hidden sm:inline ml-1.5 text-xs whitespace-nowrap ${
              isCurrent
                ? 'text-gray-900 dark:text-white font-medium'
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              {item.label}
            </span>
            {index < STEP_CONFIG.length - 1 && (
              <div className={`w-4 sm:w-6 h-0.5 mx-1.5 sm:mx-2 ${
                isCompleted ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );

  // Render Step 0 - Select Rewrite Type
  const renderSelectTypeStep = () => (
    <div className="space-y-3 sm:space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Select the type of rewrite you want to perform:
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {Object.entries(REWRITE_TYPE_CONFIG).map(([type, config]) => {
          const isSelected = rewriteType === type;

          return (
            <button
              key={type}
              type="button"
              onClick={() => handleRewriteTypeSelect(type)}
              className={`p-3 sm:p-4 rounded-xl border-2 text-left transition-all active:scale-[0.98] ${
                isSelected
                  ? 'border-primary bg-primary/10 dark:bg-primary/20 ring-2 ring-primary/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-1 sm:mb-2">
                <h4 className={`font-semibold text-sm sm:text-base ${
                  isSelected ? 'text-primary dark:text-primary-light' : 'text-gray-900 dark:text-white'
                }`}>
                  {config.label}
                </h4>
                {!config.supported && (
                  <span className="text-[10px] sm:text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap">
                    Coming Soon
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {config.description}
              </p>
            </button>
          );
        })}
      </div>

      {errors.rewriteType && (
        <p className="text-xs text-red-500 mt-2">{errors.rewriteType}</p>
      )}
    </div>
  );

  // Render Step 1 - Select Original Power(s)
  // Original powers are entered manually and validated via API lookup
  // They come from executed powers (E, T, F, J, D status) NOT in the current report
  const renderSelectOriginalsStep = () => {
    const isMultiSelect = rewriteType === REWRITE_TYPES.MANY_TO_ONE;
    const config = REWRITE_TYPE_CONFIG[rewriteType];

    // Handle Enter key to add power
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && originalPowerInput.trim()) {
        e.preventDefault();
        lookupOriginalPower(originalPowerInput);
      }
    };

    return (
      <div className="space-y-4">
        {/* Instructions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 sm:p-3 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2">
            <InformationCircleIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-300">
              {isMultiSelect
                ? 'Enter executed power numbers (E, T, F, J, D status) to combine. All must share the same defendant.'
                : 'Enter an executed power number (E, T, F, J, D status) to find the original to rewrite.'}
            </p>
          </div>
        </div>

        {/* Power Number Input */}
        {isAtMaxOriginals ? (
          <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <InformationCircleIcon className="h-5 w-5 text-primary flex-shrink-0" />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Maximum {currentTypeConfig.maxOriginals} original power{currentTypeConfig.maxOriginals !== 1 ? 's' : ''} selected for {currentTypeConfig.label}. Remove a power to add a different one.
            </span>
          </div>
        ) : (
          <div>
            <label htmlFor="rewrite-powerNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Power Number *
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  id="rewrite-powerNumber"
                  type="text"
                  value={originalPowerInput}
                  onChange={(e) => {
                    setOriginalPowerInput(e.target.value.toUpperCase());
                    setOriginalLookupError('');
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter power number (e.g., 123456789)"
                  className={`w-full px-4 py-3 sm:py-2 border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-base sm:text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                    originalLookupError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  disabled={lookingUpOriginal}
                />
                {lookingUpOriginal && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <OathSpinner size="sm" />
                  </div>
                )}
              </div>
              <OathButton
                type="button"
                onClick={() => lookupOriginalPower(originalPowerInput)}
                disabled={!originalPowerInput.trim() || lookingUpOriginal}
                className="px-4 py-3 sm:py-2 whitespace-nowrap"
              >
                {lookingUpOriginal ? 'Looking up...' : 'Add Power'}
              </OathButton>
            </div>
            {originalLookupError && (
              <p className="text-xs text-red-500 mt-1">{originalLookupError}</p>
            )}
          </div>
        )}

        {/* Selected Powers List */}
        {selectedOriginalPowers.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Selected Original Power{selectedOriginalPowers.length !== 1 ? 's' : ''} ({selectedOriginalPowers.length})
              </h4>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {selectedOriginalPowers.map((power, index) => (
                <div
                  key={power.powerNumber || index}
                  className="flex items-center gap-3 p-3 border-b border-gray-100 dark:border-gray-700 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-medium text-gray-900 dark:text-white">
                        {power.powerNumber}
                      </span>
                      {/* Status badge */}
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        power.status === 'F' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                        power.status === 'J' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                        power.status === 'T' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                        'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      }`}>
                        {power.status === 'E' ? 'Executed' :
                         power.status === 'F' ? 'Forfeiture' :
                         power.status === 'J' ? 'Judgment' :
                         power.status === 'T' ? 'Transfer' :
                         power.status === 'D' ? 'Discharge' : power.status}
                      </span>
                      {power.hasTransferBond && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                          Transfer Bond
                        </span>
                      )}
                      {power._isManualEntry && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                          Manual Entry
                        </span>
                      )}
                      {!power._isManualEntry && power._hasHistoricalData === false && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                          No Report History
                        </span>
                      )}
                    </div>
                    {power._isManualEntry ? (
                      <div className="text-sm text-amber-600 dark:text-amber-400 mt-0.5">
                        Pending backend validation
                      </div>
                    ) : power._hasHistoricalData === false ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        Power not previously reported - defendant info unavailable
                      </div>
                    ) : (
                      <>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                          {power.defendantFirstName} {power.defendantLastName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDateForDisplay(power.executionDate)}
                        </div>
                      </>
                    )}
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    ${formatCurrency(power.powerAmount || power.bondAmount)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeOriginalPower(power.powerNumber)}
                    className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Remove power"
                    aria-label="Remove power"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selection Summary */}
        {selectedOriginalPowers.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                {selectedOriginalPowers.length} power{selectedOriginalPowers.length !== 1 ? 's' : ''} selected
                {config && ` (min: ${config.minOriginals}${config.maxOriginals ? `, max: ${config.maxOriginals}` : ''})`}
              </span>
              <span className="font-medium text-gray-900 dark:text-white">
                Total: ${formatCurrency(totalOriginalAmount)}
              </span>
            </div>
            {/* Warning for F/J status powers */}
            {hasForfeitureOrJudgment && (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <span className="text-xs text-amber-700 dark:text-amber-400">
                  Forfeiture/Judgment power(s) selected. Case number must be validated.
                </span>
              </div>
            )}
            {/* Info for transfer bond */}
            {hasTransferBond && (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <InformationCircleIcon className="h-4 w-4 text-purple-500 flex-shrink-0" />
                <span className="text-xs text-purple-700 dark:text-purple-400">
                  Transfer bond detected. Backend will handle transfer bond processing.
                </span>
              </div>
            )}
            {/* Warning for manual entries (API not available) */}
            {selectedOriginalPowers.some(p => p._isManualEntry) && (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <span className="text-xs text-amber-700 dark:text-amber-400">
                  Manual entry - backend lookup API not available. Power will be validated when rewrite is executed.
                </span>
              </div>
            )}
          </div>
        )}

        {/* Case Number Verification — required for F/J powers or any power with a case number on file */}
        {selectedOriginalPowers.length > 0 && requiresCaseVerification && !caseNumberVerified && (
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
            <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
              Verify Case Number
            </h4>
            <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">
              Please enter the case number from the power you selected to verify.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={caseNumberVerificationInput}
                onChange={(e) => {
                  setCaseNumberVerificationInput(e.target.value.toUpperCase());
                  setCaseNumberVerificationError('');
                }}
                placeholder="Enter case number"
                className="flex-1 px-4 py-2.5 text-sm border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                maxLength={25}
              />
              <button
                type="button"
                onClick={handleVerifyCaseNumber}
                className="px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-medium transition-colors"
              >
                Verify
              </button>
            </div>
            {caseNumberVerificationError && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                {caseNumberVerificationError}
              </p>
            )}
          </div>
        )}

        {/* Verification Success Indicator */}
        {selectedOriginalPowers.length > 0 && requiresCaseVerification && caseNumberVerified && (
          <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 border border-green-200 dark:border-green-800 flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            <span className="text-sm text-green-800 dark:text-green-300">Case number verified</span>
          </div>
        )}

        {errors.originalPower && (
          <p className="text-xs text-red-500 mt-2">{errors.originalPower}</p>
        )}
      </div>
    );
  };

  // Render Step 2 - Select Replacement Power
  const renderSelectReplacementStep = () => {
    const isOneToMany = rewriteType === REWRITE_TYPES.ONE_TO_MANY;

    // Filter powers for a specific search term (for One-to-Many)
    const getFilteredPowers = (searchTerm) => {
      const powers = Array.isArray(availablePowers) ? availablePowers : [];
      if (!searchTerm) return powers;
      const search = searchTerm.toLowerCase();
      return powers.filter(p =>
        p.powerNumber?.toLowerCase().includes(search) ||
        p.agencyName?.toLowerCase().includes(search)
      );
    };

    // One-to-Many: Multiple replacement power selection
    if (isOneToMany) {
      return (
        <div className="space-y-4">
          {/* Original Power Summary */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Splitting Power
            </h4>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-mono">{selectedOriginalPowers[0]?.powerNumber}</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                (${formatCurrency(totalOriginalAmount)})
              </span>
            </div>
          </div>

          {/* Info about One-to-Many */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 sm:p-3 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2">
              <InformationCircleIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-300">
                Select at least 2 replacement powers. The original will be split across these.
              </p>
            </div>
          </div>

          {/* Unified Replacement Slots */}
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {replacementSearches.map((searchTerm, index) => {
              const selectedPower = replacementPowers[index];
              const filtered = getFilteredPowers(searchTerm);

              return (
                <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-xl p-3">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Replacement #{index + 1}
                  </label>
                  {selectedPower ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                        <span className="font-mono text-sm text-gray-900 dark:text-white truncate">{selectedPower.powerNumber}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-sm text-gray-700 dark:text-gray-300">${selectedPower.denomination?.toLocaleString() || 'N/A'}</span>
                        {replacementSearches.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveReplacementSlot(index)}
                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1"
                            title="Remove"
                            aria-label="Remove"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => { handleReplacementSearchChange(e.target.value, index); setHighlightedReplacementPowerIndex(0); }}
                            onFocus={() => { setActiveReplacementIndex(index); setHighlightedReplacementPowerIndex(0); }}
                            onKeyDown={(e) => handleReplacementSlotKeyDown(e, filtered.slice(0, 10), index)}
                            placeholder="Search power number..."
                            className="w-full px-4 py-2 border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary border-gray-300 dark:border-gray-600"
                          />
                          {loadingPowers && activeReplacementIndex === index && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <OathSpinner size="sm" />
                            </div>
                          )}
                          {/* Dropdown */}
                          {activeReplacementIndex === index && searchTerm && filtered.length > 0 && (
                            <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                              {filtered.slice(0, 10).map((p, itemIndex) => (
                                <button
                                  key={p.powerNumber}
                                  type="button"
                                  onClick={() => handleOneToManyReplacementSelected(p, index)}
                                  className={`w-full px-4 py-2 text-left border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                                    itemIndex === highlightedReplacementPowerIndex
                                      ? 'bg-primary/10 dark:bg-primary/20'
                                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                                  }`}
                                >
                                  <div className="flex justify-between items-center gap-2">
                                    <span className="flex items-center gap-2 min-w-0">
                                      <span className="font-mono text-sm text-gray-900 dark:text-white">{p.powerNumber}</span>
                                      <ReplacementStatusBadge status={p.powerStatusCd} />
                                    </span>
                                    <span className="text-xs text-gray-500">${p.denomination?.toLocaleString() || 'N/A'}</span>
                                  </div>
                                  {p.agencyName && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{p.agencyName}</p>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {replacementSearches.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveReplacementSlot(index)}
                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1 flex-shrink-0"
                            title="Remove"
                            aria-label="Remove"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Type at least 3 characters to search available inventory
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add Another Button */}
          <button
            type="button"
            onClick={handleAddReplacementSlot}
            className="w-full flex items-center justify-center gap-2 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 hover:border-primary hover:text-primary transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Add Another Replacement
          </button>

          {/* Selection Summary */}
          {replacementPowers.filter(p => p).length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  Selected: {replacementPowers.filter(p => p).length} replacement{replacementPowers.filter(p => p).length !== 1 ? 's' : ''}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  Total Denomination: ${replacementPowers.filter(p => p).reduce((sum, p) => sum + (p?.denomination || 0), 0).toLocaleString()}
                </span>
              </div>
            </div>
          )}

          {errors.powerNumber && (
            <p className="text-xs text-red-500 mt-1">{errors.powerNumber}</p>
          )}
        </div>
      );
    }

    // One-to-One and Many-to-One: Single replacement power selection
    return (
      <div className="space-y-4">
        {/* Original Power Summary */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {selectedOriginalPowers.length === 1 ? 'Replacing Power' : `Replacing ${selectedOriginalPowers.length} Powers`}
          </h4>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {selectedOriginalPowers.map((p, i) => (
              <span key={p.powerNumber || p.powerReportPowerId || i}>
                {i > 0 && ', '}
                <span className="font-mono">{p.powerNumber}</span>
              </span>
            ))}
            <span className="ml-2 font-medium text-gray-900 dark:text-white">
              (${formatCurrency(totalOriginalAmount)})
            </span>
          </div>
        </div>

        {/* Select Replacement Power */}
        <div>
          <label htmlFor="rewrite-replacementPower" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Select Replacement Power *
          </label>
          <div className="relative" ref={powerDropdownRef}>
            <input
              id="rewrite-replacementPower"
              type="text"
              role="combobox"
              aria-expanded={showPowerDropdown}
              aria-controls="rewrite-power-listbox"
              aria-activedescendant={highlightedPowerIndex >= 0 ? `rewrite-power-option-${highlightedPowerIndex}` : undefined}
              value={powerSearch}
              onChange={(e) => {
                setPowerSearch(e.target.value);
                setShowPowerDropdown(true);
                setHighlightedPowerIndex(0);
              }}
              onFocus={() => { setShowPowerDropdown(true); setHighlightedPowerIndex(0); }}
              onKeyDown={handleReplacementPowerKeyDown}
              placeholder="Search power number..."
              className={`w-full px-4 py-2 border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                errors.powerNumber ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {loadingPowers && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <OathSpinner size="sm" />
              </div>
            )}
            {showPowerDropdown && filteredReplacementPowers.length > 0 && (
              <div ref={powerListRef} id="rewrite-power-listbox" role="listbox" className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {filteredReplacementPowers.map((p, index) => (
                  <button
                    key={p.powerNumber}
                    id={`rewrite-power-option-${index}`}
                    role="option"
                    aria-selected={index === highlightedPowerIndex}
                    type="button"
                    onClick={() => handleReplacementPowerSelected(p)}
                    className={`w-full px-4 py-3 text-left border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                      index === highlightedPowerIndex
                        ? 'bg-primary/10 dark:bg-primary/20'
                        : selectedReplacementPower?.powerNumber === p.powerNumber
                          ? 'bg-primary/10'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex justify-between items-center gap-2">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="font-mono font-medium text-gray-900 dark:text-white">
                          {p.powerNumber}
                        </span>
                        <ReplacementStatusBadge status={p.powerStatusCd} />
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ${p.denomination?.toLocaleString() || 'N/A'}
                      </span>
                    </div>
                    {p.agencyName && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {p.agencyName}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
            {showPowerDropdown && loadingPowers && (
              <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4 text-center text-gray-500 dark:text-gray-400">
                <div className="flex items-center justify-center gap-2">
                  <OathSpinner size="sm" />
                  <span>Loading available powers...</span>
                </div>
              </div>
            )}
            {showPowerDropdown && filteredReplacementPowers.length === 0 && !loadingPowers && (
              <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4 text-center text-gray-500 dark:text-gray-400">
                {powerSearch.length < 3 ? 'Type at least 3 characters to search' : 'No available powers found'}
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Type at least 3 characters to search available inventory
          </p>
          {errors.powerNumber && (
            <p className="text-xs text-red-500 mt-1">{errors.powerNumber}</p>
          )}
        </div>

        {/* Selected Power Preview */}
        {selectedReplacementPower && (
          <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-green-800 dark:text-green-300">
                  Selected Replacement Power
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedReplacementPower(null);
                  setFormData(prev => ({ ...prev, powerNumber: '' }));
                  setPowerSearch('');
                }}
                className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title="Remove selection"
                aria-label="Remove selection"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-green-700 dark:text-green-400">Power Number:</span>
                <span className="ml-2 font-mono font-medium text-green-900 dark:text-green-200">
                  {selectedReplacementPower.powerNumber}
                </span>
              </div>
              <div>
                <span className="text-green-700 dark:text-green-400">Denomination:</span>
                <span className="ml-2 font-medium text-green-900 dark:text-green-200">
                  ${selectedReplacementPower.denomination?.toLocaleString() || 'N/A'}
                </span>
              </div>
              {selectedReplacementPower.powerStatusCd && selectedReplacementPower.powerStatusCd !== 'U' && (
                <div className="col-span-2">
                  <span className="text-green-700 dark:text-green-400">Status:</span>
                  <ReplacementStatusBadge status={selectedReplacementPower.powerStatusCd} className="ml-2" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Step 3 - Enter Details
  const renderDetailsStep = () => {
    const isManyToOne = rewriteType === REWRITE_TYPES.MANY_TO_ONE;
    const isOneToMany = rewriteType === REWRITE_TYPES.ONE_TO_MANY;
    const validReplacements = isOneToMany ? replacementPowers.filter(p => p) : [];
    const activePower = isOneToMany ? replacementPowers[activeBondIndex] : null;
    const activeBond = isOneToMany ? perBondData[activeBondIndex] : null;
    // For One-to-Many, render bond-amount/exec-date/sc inputs from per-bond state.
    // For others, use the shared formData. Display strings are kept locally on
    // perBondData entries (bondAmountDisplay, executionDateText, scAmountDisplay).
    const bondAmountValue = isOneToMany ? (activeBond?.bondAmountDisplay || '') : bondAmountDisplay;
    const executionDateValue = isOneToMany ? (activeBond?.executionDateText || '') : executionDateText;
    const executionDateForPicker = isOneToMany
      ? (formatDateForInput(activeBond?.executionDate) || '')
      : (formatDateForInput(formData.executionDate) || '');
    const scAmountValue = isOneToMany ? (activeBond?.scAmountDisplay || '') : scAmountDisplay;
    const sharedHelpText = isOneToMany ? 'Applies to all bonds' : null;

    return (
    <div className="space-y-2 sm:space-y-3">
      {/* Many-to-One: Show powers being combined */}
      {isManyToOne && selectedOriginalPowers.length > 1 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2 sm:p-3 border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2 flex-wrap">
            <InformationCircleIcon className="h-4 w-4 text-amber-700 dark:text-amber-400 flex-shrink-0" />
            <span className="text-xs text-amber-800 dark:text-amber-300 font-medium">Combining {selectedOriginalPowers.length} Powers:</span>
            {selectedOriginalPowers.map((p, i) => (
              <span
                key={p.powerNumber || p.powerReportPowerId || i}
                className="inline-flex items-center gap-1 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 rounded"
              >
                <span className="font-mono">{p.powerNumber}</span>
                <span className="text-amber-600 dark:text-amber-400">(${formatCurrency(p.powerAmount)})</span>
              </span>
            ))}
            <span className="text-xs text-amber-700 dark:text-amber-400">
              Total: <span className="font-medium">${formatCurrency(totalOriginalAmount)}</span>
            </span>
          </div>
        </div>
      )}

      {/* One-to-Many: Show power being split + per-bond sub-step indicator */}
      {isOneToMany && validReplacements.length >= 2 && (
        <>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 sm:p-3 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 flex-wrap">
              <InformationCircleIcon className="h-4 w-4 text-blue-700 dark:text-blue-400 flex-shrink-0" />
              <span className="text-xs text-blue-800 dark:text-blue-300 font-medium">Splitting into {validReplacements.length} Powers:</span>
              {validReplacements.map((p, i) => {
                const isActive = activePower?.powerNumber === p.powerNumber;
                return (
                  <span
                    key={i}
                    className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${
                      isActive
                        ? 'bg-blue-600 text-white ring-2 ring-blue-300 dark:ring-blue-500'
                        : 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300'
                    }`}
                  >
                    <span className="font-mono">{p.powerNumber}</span>
                    <span className={isActive ? 'text-blue-100' : 'text-blue-600 dark:text-blue-400'}>
                      (${p.denomination?.toLocaleString() || 'N/A'})
                    </span>
                  </span>
                );
              })}
              <span className="text-xs text-blue-700 dark:text-blue-400">
                from <span className="font-mono">{selectedOriginalPowers[0]?.powerNumber}</span> (${formatCurrency(totalOriginalAmount)})
              </span>
            </div>
          </div>

          {/* Sub-step indicator: which bond is being edited */}
          {activePower && (
            <div className="flex items-center justify-between gap-2 px-1">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Bond {activeBondIndex + 1} of {validReplacements.length}
                <span className="ml-2 font-mono font-normal text-gray-600 dark:text-gray-400">
                  &mdash; Power {activePower.powerNumber}
                </span>
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                Enter the bond amount and execution date for this bond
              </span>
            </div>
          )}
        </>
      )}

      {/* Bond Amount and Execution Date */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        <div>
          <label htmlFor="rewrite-bondAmount" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Bond Amount *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
            <input
              id="rewrite-bondAmount"
              type="text"
              inputMode="decimal"
              value={bondAmountValue}
              onChange={(e) => isOneToMany
                ? handlePerBondCurrencyChange('bondAmount', 'bondAmountDisplay', e.target.value)
                : handleCurrencyChange('bondAmount', e.target.value, setBondAmountDisplay)}
              placeholder="0.00"
              className={`w-full pl-8 pr-4 py-2 sm:py-1.5 text-sm border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                errors.bondAmount ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
          </div>
          {errors.bondAmount && (
            <p className="text-xs text-red-500 mt-1">{errors.bondAmount}</p>
          )}
        </div>

        <div>
          <label htmlFor="rewrite-executionDate" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Execution Date *
          </label>
          <div className="relative">
            <input
              id="rewrite-executionDate"
              type="text"
              value={executionDateValue}
              onChange={(e) => isOneToMany
                ? handlePerBondDateTextChange(e.target.value)
                : handleDateTextChange('executionDate', e.target.value)}
              onBlur={() => isOneToMany ? handlePerBondDateBlur() : handleDateTextBlur('executionDate')}
              placeholder="MM/DD/YYYY"
              maxLength={10}
              className={`w-full px-4 py-2 sm:py-1.5 pr-12 text-sm border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                errors.executionDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => executionDatePickerRef.current?.showPicker()}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              title="Open date picker"
              aria-label="Open date picker"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
            <input
              type="date"
              ref={executionDatePickerRef}
              tabIndex={-1}
              value={executionDateForPicker}
              min={(() => {
                const latestOrig = selectedOriginalPowers.reduce((latest, p) => {
                  if (!p.executionDate) return latest;
                  const d = new Date(p.executionDate);
                  return (!latest || d > latest) ? d : latest;
                }, null);
                return latestOrig ? formatDateForInput(latestOrig) : undefined;
              })()}
              max={formatDateForInput(new Date())}
              onChange={(e) => {
                if (e.target.value) {
                  const dateValue = new Date(e.target.value + 'T00:00:00');
                  if (isOneToMany) {
                    handlePerBondDatePickerChange(dateValue);
                  } else {
                    handleFieldChange('executionDate', dateValue);
                    setExecutionDateText(formatDateForDisplay(dateValue));
                  }
                }
              }}
              className="sr-only"
            />
          </div>
          {errors.executionDate && (
            <p className="text-xs text-red-500 mt-1">{errors.executionDate}</p>
          )}
        </div>
      </div>

      {/* Defendant Name (read-only - inherited from original power) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Defendant First Name
            {sharedHelpText && <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">({sharedHelpText})</span>}
          </label>
          <div className="w-full px-3 py-2 sm:py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white cursor-not-allowed">
            {formData.defendantFirstName || '\u00A0'}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Defendant Last Name
            {sharedHelpText && <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">({sharedHelpText})</span>}
          </label>
          <div className="w-full px-3 py-2 sm:py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white cursor-not-allowed">
            {formData.defendantLastName || '\u00A0'}
          </div>
        </div>
      </div>

      {/* SSN, DOB, Case Number */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
        <div>
          <label htmlFor="rewrite-ssn" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            SSN
          </label>
          <input
            id="rewrite-ssn"
            type="text"
            value={formData.defendantSsn}
            onChange={(e) => handleFieldChange('defendantSsn', formatSSN(e.target.value))}
            placeholder="XXX-XX-XXXX"
            maxLength={VALIDATION_RULES.MAX_SSN_LENGTH}
            className={`w-full px-3 py-2 sm:py-1.5 text-sm border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
              errors.defendantSsn ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
          />
          {errors.defendantSsn && (
            <p className="text-xs text-red-500 mt-1">{errors.defendantSsn}</p>
          )}
        </div>

        <div>
          <label htmlFor="rewrite-dob" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Date of Birth
          </label>
          <div className="relative">
            <input
              id="rewrite-dob"
              type="text"
              value={dobText}
              onChange={(e) => handleDateTextChange('defendantDob', e.target.value)}
              onBlur={() => handleDateTextBlur('defendantDob')}
              placeholder="MM/DD/YYYY"
              maxLength={10}
              className="w-full px-3 py-2 sm:py-1.5 pr-10 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => dobDatePickerRef.current?.showPicker()}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
              title="Open date picker"
              aria-label="Open date picker"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
            <input
              type="date"
              ref={dobDatePickerRef}
              tabIndex={-1}
              value={formatDateForInput(formData.defendantDob) || ''}
              onChange={(e) => {
                if (e.target.value) {
                  const dateValue = new Date(e.target.value + 'T00:00:00');
                  handleFieldChange('defendantDob', dateValue);
                  setDobText(formatDateForDisplay(dateValue));
                }
              }}
              className="sr-only"
            />
          </div>
        </div>

        <div>
          <label htmlFor="rewrite-caseNumber" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Case Number
            {sharedHelpText && <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">({sharedHelpText})</span>}
          </label>
          <input
            id="rewrite-caseNumber"
            type="text"
            value={formData.caseNumber}
            onChange={(e) => {
              handleFieldChange('caseNumber', e.target.value.toUpperCase());
            }}
            placeholder="Case #"
            maxLength={VALIDATION_RULES.MAX_CASE_NUMBER_LENGTH}
            className="w-full px-3 py-2 sm:py-1.5 text-sm border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary border-gray-300 dark:border-gray-600"
          />
        </div>
      </div>

      {/* Executing Agent and Court City */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        <div>
          <label htmlFor="rewrite-executingAgent" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Executing Agent *
            {sharedHelpText && <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">({sharedHelpText})</span>}
          </label>
          <div className="relative" ref={agentDropdownRef}>
            <input
              id="rewrite-executingAgent"
              type="text"
              role="combobox"
              aria-expanded={showAgentDropdown}
              aria-controls="rewrite-agent-listbox"
              aria-activedescendant={highlightedAgentIndex >= 0 ? `rewrite-agent-option-${highlightedAgentIndex}` : undefined}
              value={agentSearch}
              onChange={(e) => {
                setAgentSearch(lettersOnly(e.target.value));
                setShowAgentDropdown(true);
                setHighlightedAgentIndex(0);
              }}
              onFocus={() => {
                if (selectedAgent) {
                  const currentText = `${selectedAgent.firstName} ${selectedAgent.lastName}`;
                  if (agentSearch === currentText) setAgentSearch('');
                }
                setShowAgentDropdown(true);
                setHighlightedAgentIndex(0);
              }}
              onBlur={(e) => {
                if (agentDropdownRef.current?.contains(e.relatedTarget)) return;
                if (selectedAgent) {
                  setAgentSearch(`${selectedAgent.firstName} ${selectedAgent.lastName}`);
                } else {
                  setAgentSearch('');
                }
              }}
              onKeyDown={handleAgentKeyDown}
              placeholder="Search agent..."
              autoComplete="off"
              className={`w-full px-3 py-2 sm:py-1.5 pr-9 text-sm border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                errors.executingAgentId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-gray-500 dark:text-gray-400" />
            {showAgentDropdown && filteredAgents.length > 0 && (
              <div
                ref={agentListRef}
                id="rewrite-agent-listbox"
                role="listbox"
                className="absolute z-20 w-full bottom-full mb-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-y-auto"
              >
                {filteredAgents.map((a, index) => (
                  <button
                    key={a.executingAgentId}
                    id={`rewrite-agent-option-${index}`}
                    role="option"
                    aria-selected={index === highlightedAgentIndex}
                    type="button"
                    onClick={() => handleAgentSelected(a)}
                    className={`w-full px-4 py-2 text-left text-gray-900 dark:text-white ${
                      index === highlightedAgentIndex
                        ? 'bg-primary/10 dark:bg-primary/20'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {a.firstName} {a.lastName}
                  </button>
                ))}
              </div>
            )}
          </div>
          {errors.executingAgentId && (
            <p className="text-xs text-red-500 mt-1">{errors.executingAgentId}</p>
          )}
          {!errors.executingAgentId && warnings.executingAgentId && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{warnings.executingAgentId}</p>
          )}
        </div>

        <div>
          <label htmlFor="rewrite-courtCity" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Court City *
            {sharedHelpText && <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">({sharedHelpText})</span>}
          </label>
          <div className="relative" ref={cityDropdownRef}>
            <input
              id="rewrite-courtCity"
              type="text"
              role="combobox"
              aria-expanded={showCityDropdown}
              aria-controls="rewrite-city-listbox"
              aria-activedescendant={highlightedCityIndex >= 0 ? `rewrite-city-option-${highlightedCityIndex}` : undefined}
              value={citySearch}
              onChange={(e) => {
                setCitySearch(lettersOnly(e.target.value));
                setShowCityDropdown(true);
                setHighlightedCityIndex(0);
              }}
              onFocus={() => {
                if (selectedCity) {
                  const currentText = `${toTitleCase(selectedCity.name)}, ${selectedCity.stateCode}`;
                  if (citySearch === currentText) setCitySearch('');
                }
                setShowCityDropdown(true);
                setHighlightedCityIndex(0);
              }}
              onBlur={(e) => {
                if (cityDropdownRef.current?.contains(e.relatedTarget)) return;
                if (selectedCity) {
                  setCitySearch(`${toTitleCase(selectedCity.name)}, ${selectedCity.stateCode}`);
                } else {
                  setCitySearch('');
                }
              }}
              onKeyDown={handleCityKeyDown}
              placeholder="Search city..."
              autoComplete="off"
              className={`w-full px-3 py-2 sm:py-1.5 pr-9 text-sm border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                errors.cityId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-gray-500 dark:text-gray-400" />
            {showCityDropdown && filteredCities.length > 0 && (
              <div
                ref={cityListRef}
                id="rewrite-city-listbox"
                role="listbox"
                className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-y-auto"
              >
                {filteredCities.map((c, index) => (
                  <button
                    key={`${c.name}-${c.stateCode}`}
                    id={`rewrite-city-option-${index}`}
                    role="option"
                    aria-selected={index === highlightedCityIndex}
                    type="button"
                    onClick={() => handleCitySelected(c)}
                    className={`w-full px-4 py-2 text-left text-gray-900 dark:text-white ${
                      index === highlightedCityIndex
                        ? 'bg-primary/10 dark:bg-primary/20'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {toTitleCase(c.name)}, {c.stateCode}
                  </button>
                ))}
              </div>
            )}
          </div>
          {errors.cityId && (
            <p className="text-xs text-red-500 mt-1">{errors.cityId}</p>
          )}
          {!errors.cityId && warnings.cityId && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{warnings.cityId}</p>
          )}
        </div>
      </div>

      {/* SC Amount and Offense */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        {/* SC Amount - only show if SC state */}
        {formData.executingState?.toUpperCase() === 'SC' && (
          <div>
            <label htmlFor="rewrite-scPremium" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Actual SC Premium
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                id="rewrite-scPremium"
                type="text"
                inputMode="decimal"
                value={scAmountValue}
                onChange={(e) => isOneToMany
                  ? handlePerBondCurrencyChange('scAmount', 'scAmountDisplay', e.target.value)
                  : handleCurrencyChange('scAmount', e.target.value, setScAmountDisplay)}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-2 sm:py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>
        )}

        <div className={formData.executingState?.toUpperCase() === 'SC' ? '' : 'sm:col-span-2'}>
          <label htmlFor="rewrite-offense" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Offense
            {sharedHelpText && <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">({sharedHelpText})</span>}
          </label>
          <input
            id="rewrite-offense"
            type="text"
            value={formData.offense}
            onChange={(e) => handleFieldChange('offense', e.target.value.toUpperCase())}
            maxLength={VALIDATION_RULES.MAX_OFFENSE_LENGTH}
            placeholder="Enter offense"
            className="w-full px-3 py-2 sm:py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
      </div>
    </div>
    );
  };

  // Render Step 4 - Review
  const renderReviewStep = () => (
    <div className="space-y-4">
      {/* Type Badge and Unsupported Warning */}
      <div className="flex items-center justify-between">
        <span className={`text-xs px-2 py-1 rounded-full ${
          currentTypeConfig?.supported
            ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-light'
            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
        }`}>
          {currentTypeConfig?.label}
        </span>
      </div>

      {/* Unsupported Type Warning */}
      {!currentTypeConfig?.supported && (
        <OathAlert variant="warning">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{currentTypeConfig?.label} Not Yet Supported</p>
              <p className="text-sm mt-1">
                This rewrite type is not yet supported by the backend. Please use One-to-One rewrites for now.
              </p>
            </div>
          </div>
        </OathAlert>
      )}

      {/* Warning for submitted reports */}
      {isSubmittedReport && currentTypeConfig?.supported && (
        <OathAlert variant="warning">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Submitted Report Warning</p>
              <p className="text-sm mt-1">
                This report has already been submitted. This rewrite will create transaction records
                and may result in financial adjustments.
              </p>
            </div>
          </div>
        </OathAlert>
      )}

      {/* Validation warnings */}
      {validationResult?.warnings?.length > 0 && (
        <OathAlert variant="info">
          <div className="space-y-1">
            {validationResult.warnings.map((warning, index) => (
              <p key={index} className="text-sm">{warning}</p>
            ))}
          </div>
        </OathAlert>
      )}

      {/* Comparison: Original vs Replacement */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {/* Original Power(s) */}
        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 sm:p-4 border border-red-200 dark:border-red-800">
          <h4 className="text-xs sm:text-sm font-medium text-red-800 dark:text-red-300 mb-2 sm:mb-3 flex items-center gap-2">
            <ArrowLeftIcon className="h-4 w-4 flex-shrink-0" />
            <span className="truncate flex-1">Original{selectedOriginalPowers.length > 1 ? 's' : ''} (Replaced)</span>
            <button
              type="button"
              onClick={() => setCurrentStep(REWRITE_STEPS.SELECT_ORIGINALS)}
              className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors flex-shrink-0"
              title="Edit original power selection"
            >
              <PencilSquareIcon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Edit</span>
            </button>
          </h4>
          <div className="space-y-2">
            {selectedOriginalPowers.map((power, i) => (
              <div key={power.powerNumber || power.powerReportPowerId || i} className="text-xs sm:text-sm space-y-1">
                <div className="flex justify-between gap-2">
                  <span className="text-red-700 dark:text-red-400">Power:</span>
                  <span className="font-mono font-medium text-red-900 dark:text-red-200 truncate">
                    {power.powerNumber}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-red-700 dark:text-red-400">Bond Amt:</span>
                  <span className="font-medium text-red-900 dark:text-red-200">
                    {formatCurrencyWithSymbol(power.powerAmount)}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-red-700 dark:text-red-400 flex-shrink-0">Defendant:</span>
                  <span className="text-red-900 dark:text-red-200 truncate text-right">
                    {power.defendantFirstName} {power.defendantLastName}
                  </span>
                </div>
                {selectedOriginalPowers.length > 1 && (
                  <div className="border-b border-red-200 dark:border-red-800 my-2" />
                )}
              </div>
            ))}
            {selectedOriginalPowers.length > 1 && (
              <div className="pt-2 border-t border-red-200 dark:border-red-800">
                <div className="flex justify-between font-medium">
                  <span className="text-red-700 dark:text-red-400">Total:</span>
                  <span className="text-red-900 dark:text-red-200">
                    {formatCurrencyWithSymbol(totalOriginalAmount)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Replacement Power(s) */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 sm:p-4 border border-green-200 dark:border-green-800">
          <h4 className="text-xs sm:text-sm font-medium text-green-800 dark:text-green-300 mb-2 sm:mb-3 flex items-center gap-2">
            <ArrowRightIcon className="h-4 w-4 flex-shrink-0" />
            <span className="truncate flex-1">Replacement{rewriteType === REWRITE_TYPES.ONE_TO_MANY ? 's' : ''} (New)</span>
            <button
              type="button"
              onClick={() => setCurrentStep(REWRITE_STEPS.ENTER_DETAILS)}
              className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors flex-shrink-0"
              title="Edit replacement details"
            >
              <PencilSquareIcon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Edit</span>
            </button>
          </h4>
          {rewriteType === REWRITE_TYPES.ONE_TO_MANY ? (
            // One-to-Many: each replacement bond shows its own amount and exec date
            (() => {
              const validReps = replacementPowers.filter(p => p);
              const bondFor = (powerNumber) => perBondData.find(b => b?._powerNumber === powerNumber);
              const totalReplacementAmount = validReps.reduce((sum, p) => {
                const b = bondFor(p.powerNumber);
                return sum + (parseFloat(b?.bondAmount) || 0);
              }, 0);
              return (
                <div className="space-y-2 sm:space-y-3">
                  {validReps.map((power, index) => {
                    const bond = bondFor(power.powerNumber);
                    return (
                      <div key={power.powerNumber || index} className="text-xs sm:text-sm space-y-1">
                        <div className="flex justify-between gap-2">
                          <span className="text-green-700 dark:text-green-400">#{index + 1} Power:</span>
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="font-mono font-medium text-green-900 dark:text-green-200 truncate">
                              {power.powerNumber}
                            </span>
                            <ReplacementStatusBadge status={power.powerStatusCd} />
                          </span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-green-700 dark:text-green-400">Bond Amt:</span>
                          <span className="font-medium text-green-900 dark:text-green-200">
                            {formatCurrencyWithSymbol(bond?.bondAmount || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-green-700 dark:text-green-400">Execution Date:</span>
                          <span className="text-green-900 dark:text-green-200">
                            {bond?.executionDate ? formatDateForDisplay(bond.executionDate) : 'N/A'}
                          </span>
                        </div>
                        {formData.executingState?.toUpperCase() === 'SC' && bond?.scAmount && (
                          <div className="flex justify-between gap-2">
                            <span className="text-green-700 dark:text-green-400">SC Premium:</span>
                            <span className="text-green-900 dark:text-green-200">
                              {formatCurrencyWithSymbol(bond.scAmount)}
                            </span>
                          </div>
                        )}
                        {index < validReps.length - 1 && (
                          <div className="border-b border-green-200 dark:border-green-800 my-2" />
                        )}
                      </div>
                    );
                  })}
                  <div className="pt-2 border-t border-green-200 dark:border-green-800">
                    <div className="flex justify-between font-medium text-xs sm:text-sm">
                      <span className="text-green-700 dark:text-green-400">Total Bond Amt:</span>
                      <span className="text-green-900 dark:text-green-200">
                        {formatCurrencyWithSymbol(totalReplacementAmount)}
                      </span>
                    </div>
                  </div>
                  {/* Shared details — apply to every bond above */}
                  <div className="border-t border-green-200 dark:border-green-800 my-2" />
                  <div className="space-y-1 text-xs sm:text-sm">
                    <div className="text-[10px] sm:text-xs text-green-600 dark:text-green-400 font-medium">
                      Shared across all bonds
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-green-700 dark:text-green-400 flex-shrink-0">Defendant:</span>
                      <span className="text-green-900 dark:text-green-200 truncate text-right">
                        {formData.defendantFirstName} {formData.defendantLastName}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-green-700 dark:text-green-400">Agent:</span>
                      <span className="text-green-900 dark:text-green-200 truncate text-right">
                        {selectedAgent ? `${selectedAgent.firstName} ${selectedAgent.lastName}` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-green-700 dark:text-green-400">Court City:</span>
                      <span className="text-green-900 dark:text-green-200 truncate text-right">
                        {selectedCity ? `${toTitleCase(selectedCity.name)}, ${selectedCity.stateCode}` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-green-700 dark:text-green-400">Case #:</span>
                      <span className="text-green-900 dark:text-green-200">{formData.caseNumber || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-green-700 dark:text-green-400">SSN:</span>
                      <span className="text-green-900 dark:text-green-200">{formData.defendantSsn || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-green-700 dark:text-green-400">DOB:</span>
                      <span className="text-green-900 dark:text-green-200">
                        {formData.defendantDob ? formatDateForDisplay(formData.defendantDob) : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-green-700 dark:text-green-400">Offense:</span>
                      <span className="text-green-900 dark:text-green-200 truncate text-right">
                        {formData.offense || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            // One-to-One and Many-to-One: Single replacement power
            <div className="space-y-2 text-xs sm:text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-green-700 dark:text-green-400">Power:</span>
                <span className="font-mono font-medium text-green-900 dark:text-green-200 truncate">
                  {selectedReplacementPower?.powerNumber}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-green-700 dark:text-green-400">Bond Amt:</span>
                <span className="font-medium text-green-900 dark:text-green-200">
                  {formatCurrencyWithSymbol(formData.bondAmount)}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-green-700 dark:text-green-400 flex-shrink-0">Defendant:</span>
                <span className="text-green-900 dark:text-green-200 truncate text-right">
                  {formData.defendantFirstName} {formData.defendantLastName}
                </span>
              </div>
              {/* Merged details */}
              <div className="border-t border-green-200 dark:border-green-800 my-2" />
              <div className="flex justify-between gap-2">
                <span className="text-green-700 dark:text-green-400">Execution Date:</span>
                <span className="text-green-900 dark:text-green-200">
                  {formatDateForDisplay(formData.executionDate)}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-green-700 dark:text-green-400">Agent:</span>
                <span className="text-green-900 dark:text-green-200 truncate text-right">
                  {selectedAgent ? `${selectedAgent.firstName} ${selectedAgent.lastName}` : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-green-700 dark:text-green-400">Court City:</span>
                <span className="text-green-900 dark:text-green-200 truncate text-right">
                  {selectedCity ? `${toTitleCase(selectedCity.name)}, ${selectedCity.stateCode}` : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-green-700 dark:text-green-400">Case #:</span>
                <span className="text-green-900 dark:text-green-200">{formData.caseNumber || 'N/A'}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-green-700 dark:text-green-400">SSN:</span>
                <span className="text-green-900 dark:text-green-200">{formData.defendantSsn || 'N/A'}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-green-700 dark:text-green-400">DOB:</span>
                <span className="text-green-900 dark:text-green-200">
                  {formData.defendantDob ? formatDateForDisplay(formData.defendantDob) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-green-700 dark:text-green-400">Offense:</span>
                <span className="text-green-900 dark:text-green-200 truncate text-right">
                  {formData.offense || 'N/A'}
                </span>
              </div>
              {formData.executingState?.toUpperCase() === 'SC' && formData.scAmount && (
                <div className="flex justify-between gap-2">
                  <span className="text-green-700 dark:text-green-400">SC Premium:</span>
                  <span className="text-green-900 dark:text-green-200">
                    {formatCurrencyWithSymbol(formData.scAmount)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Financial Preview (for submitted reports) */}
      {isSubmittedReport && validationResult?.financialPreview && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 sm:p-4 border border-blue-200 dark:border-blue-800">
          <h4 className="text-xs sm:text-sm font-medium text-blue-800 dark:text-blue-300 mb-2 sm:mb-3 flex items-center gap-2">
            <InformationCircleIcon className="h-4 w-4 flex-shrink-0" />
            Financial Impact
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
            <div className="flex justify-between sm:block">
              <span className="text-blue-700 dark:text-blue-400">Bond Diff:</span>
              <span className={`sm:ml-2 font-medium ${
                validationResult.financialPreview.bondAmountDifference >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {validationResult.financialPreview.bondAmountDifference >= 0 ? '+' : ''}
                {formatCurrencyWithSymbol(validationResult.financialPreview.bondAmountDifference)}
              </span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-blue-700 dark:text-blue-400">NET Diff:</span>
              <span className={`sm:ml-2 font-medium ${
                validationResult.financialPreview.netDifference >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {validationResult.financialPreview.netDifference >= 0 ? '+' : ''}
                {formatCurrencyWithSymbol(validationResult.financialPreview.netDifference)}
              </span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-blue-700 dark:text-blue-400">BUF Diff:</span>
              <span className={`sm:ml-2 font-medium ${
                validationResult.financialPreview.bufDifference >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {validationResult.financialPreview.bufDifference >= 0 ? '+' : ''}
                {formatCurrencyWithSymbol(validationResult.financialPreview.bufDifference)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render footer buttons - mobile-friendly with proper stacking
  const renderFooter = () => (
    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full">
      <OathButton
        variant="outline"
        onClick={handleClose}
        disabled={submitting || validating}
        className="w-full sm:w-auto"
      >
        Cancel
      </OathButton>

      <div className="flex-1 hidden sm:block" />

      {currentStep > REWRITE_STEPS.SELECT_TYPE && (
        <OathButton
          variant="outline"
          onClick={handlePreviousStep}
          disabled={submitting || validating}
          icon={ArrowLeftIcon}
          className="w-full sm:w-auto order-first sm:order-none"
        >
          Back
        </OathButton>
      )}

      {currentStep < REWRITE_STEPS.REVIEW ? (
        <OathButton
          onClick={handleNextStep}
          disabled={validating}
          className="w-full sm:w-auto order-first sm:order-none"
        >
          {validating ? (
            <span className="flex items-center gap-2">
              <OathSpinner size="sm" />
              Validating...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              Next
              <ArrowRightIcon className="h-4 w-4" />
            </span>
          )}
        </OathButton>
      ) : (
        <OathButton
          variant="success"
          onClick={executeRewrite}
          disabled={submitting || !currentTypeConfig?.supported}
          className="w-full sm:w-auto order-first sm:order-none"
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <OathSpinner size="sm" />
              Processing...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <ArrowsRightLeftIcon className="h-4 w-4" />
              Confirm Rewrite
            </span>
          )}
        </OathButton>
      )}
    </div>
  );

  return (
    <>
      <OathModal
        isOpen={isOpen}
        onClose={handleClose}
        persistent={true}
        title={
          <span className="flex items-center gap-2">
            <ArrowsRightLeftIcon className="h-5 w-5" />
            Rewrite Power
          </span>
        }
        size="xl"
        footer={renderFooter()}
        allowOverflow={currentStep === REWRITE_STEPS.SELECT_REPLACEMENT || currentStep === REWRITE_STEPS.ENTER_DETAILS}
      >
        <div className="space-y-6">
          {/* Step Indicator */}
          {renderStepIndicator()}

          {/* Step Content */}
          {currentStep === REWRITE_STEPS.SELECT_TYPE && renderSelectTypeStep()}
          {currentStep === REWRITE_STEPS.SELECT_ORIGINALS && renderSelectOriginalsStep()}
          {currentStep === REWRITE_STEPS.SELECT_REPLACEMENT && renderSelectReplacementStep()}
          {currentStep === REWRITE_STEPS.ENTER_DETAILS && renderDetailsStep()}
          {currentStep === REWRITE_STEPS.REVIEW && renderReviewStep()}
        </div>
      </OathModal>

      <OathConfirmDialog
        open={showCloseConfirm}
        title="Discard rewrite progress?"
        message="All selections and entered data will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep Working"
        variant="warning"
        onConfirm={() => { setShowCloseConfirm(false); onClose(); }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </>
  );
};

export default RewritePowerModal;
